import React, { useState, useEffect, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useParams, useSearchParams, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import PoolMap from "@/components/PoolMap";
import PoolMapAdvanced from "@/components/PoolMapAdvanced";
// Sprint 74c — TemplateEditor (legacy template_content editor) removed.
// Replaced by the structured DocumentEditor + Templates page since
// Sprint 67/72. The component file has been deleted.
// ═══════════════════════════════════════════════════════════════
// Sprint 119.104 — Route-level code splitting (React.lazy)
// ═══════════════════════════════════════════════════════════════
// Objectiu: reduir bundle inicial per millorar LCP/TBT en mobile.
// PageSpeed Insights 2026-08-13: 764 KiB de JS no utilitzat.
//
// SÍNCRON (bundle inicial): LandingPageV2 (home, LCP crític),
// wrappers (LangRoute, RequireAdminAuth), UI base
// (Radix/shadcn ja imports at top), LanguageSelector (referenciat
// dins LandingPage in-file), AdminLogin (mateix fitxer que
// RequireAdminAuth — no es pot split), ARTICLES / isFeatureEnabled
// (usats inline dins <Routes>).
//
// LAZY: la resta de pàgines. El Suspense global dins <BrowserRouter>
// captura tots els lazy loads mentre carreguen el chunk.
// ═══════════════════════════════════════════════════════════════

// --- Sync (crítics per bundle inicial) ---
import AdminLogin, { RequireAdminAuth } from "./components/AdminLogin";
// Sprint 117.9 — auth gate genèric que accepta admin OR installer.
// 12/10/2026 — sense cap ruta que l'usi: /municipios/{slug}, l'única que
// el feia servir, ara es protegeix al servidor. El component es manté a
// components/RequireUserAuth.js per a la pròxima ruta que el necessiti;
// aquí no s'importa perquè aquest bloc és SÍNCRON i pesa al bundle inicial.
import LanguageSelector from "./components/LanguageSelector";
import LandingPageV2 from "./pages/LandingPageV2";
// Sprint 119.39 — Wrapper per rutes prefixades d'idioma /es/ /fr/ /de/ /it/ /en/
import LangRoute, { SUPPORTED_LANGS } from "./components/LangRoute";
// Sprint 132 — sincronitza i18n amb la URL + banner de suggeriment d'idioma
import LanguageSync from "./components/LanguageSync";
import { isFeatureEnabled } from "./config/featureFlags";
// Sprint SEO Blog v1 (2026-08-01) — Blog SEO (metadata dels articles usada inline dins Routes)
import { ARTICLES } from "./pages/blog/articles";

// --- Lazy (chunks per ruta) ---
// Auth flows / dashboards
const AdminPanel = lazy(() => import("./components/AdminPanel"));
const PoolPositionEditor = lazy(() => import("./components/PoolPositionEditor"));
const SetupPassword = lazy(() => import("./components/SetupPassword"));
const ResetPassword = lazy(() => import("./components/ResetPassword"));
const ClientAuth = lazy(() => import("./components/ClientAuth"));
const ClientDashboard = lazy(() => import("./components/ClientDashboard"));

// Public pages (contingut + landings)
const ContratacionPublicaPage = lazy(() => import("./pages/ContratacionPublicaPage"));
const MonitorPiscinasPage = lazy(() => import("./pages/MonitorPiscinasPage"));
const MunicipioPublicPage = lazy(() => import("./pages/MunicipioPublicPage"));
// Condicions tecniques per municipi (25/10/2026). El DT01 de PoolLex:
// la cara publica del pre-estudi, alimentada pel mateix nucli que
// alimentara el full A4 i la justificacio del projecte tecnic.
const CondicionsPiscinaIndex = lazy(() => import("./pages/CondicionsPiscinaIndex"));
const NormativaAguaPiscinas = lazy(() => import("./pages/NormativaAguaPiscinas"));
const CondicionsPiscinaMunicipi = lazy(() => import("./pages/CondicionsPiscinaMunicipi"));
const InitiativePage = lazy(() => import("./pages/InitiativePage"));
const SolicitarSelloPage = lazy(() => import("./pages/SolicitarSelloPage"));
const WhyPassportPage = lazy(() => import("./pages/WhyPassportPage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const EarlyAccessPage = lazy(() => import("./pages/EarlyAccessPage"));
const ComplianceCheckLanding = lazy(() => import("./pages/ComplianceCheckLanding"));
const PoolCheckQuick = lazy(() => import("./pages/PoolCheckQuick"));
const PoolCheckLanding = lazy(() => import("./pages/PoolCheckLanding"));
const InstalladoresPage = lazy(() => import("./pages/InstalladoresPage"));
const ComplianceCheckWizard = lazy(() => import("./pages/ComplianceCheckWizard"));
const ComplianceCheckResult = lazy(() => import("./pages/ComplianceCheckResult"));
const PassportGenerator = lazy(() => import("./pages/PassportGenerator"));
const PassportPublic = lazy(() => import("./pages/PassportPublic"));
const PassportEdit = lazy(() => import("./pages/PassportEdit"));
const ToolsHub = lazy(() => import("./pages/ToolsHub"));
const MantenimientoPage = lazy(() => import("./pages/MantenimientoPage"));
const ProfesionalesPage = lazy(() => import("./pages/ProfesionalesPage"));
const DocumentarPiscinaPage = lazy(() => import("./pages/DocumentarPiscinaPage"));
const PiscinaLegalPage = lazy(() => import("./pages/PiscinaLegalPage"));
const ComprarCasaPiscinaPage = lazy(() => import("./pages/ComprarCasaPiscinaPage"));
const CalculadorasHub = lazy(() => import("./pages/CalculadorasHub"));
// Sprint 119.125-lite — Portal client read-only (rol C promotor)
const ClientExpedientPortal = lazy(() => import("./pages/ClientExpedientPortal"));

// Calculadores (una per chunk — SEO landing paginado)
const VolumeCalculator = lazy(() => import("./pages/VolumeCalculator"));
const EnergyCalculator = lazy(() => import("./pages/EnergyCalculator"));
const WaterCalculator = lazy(() => import("./pages/WaterCalculator"));
const PoolSaltCalculator = lazy(() => import("./pages/PoolSaltCalculator"));
const PoolChlorineCalculator = lazy(() => import("./pages/PoolChlorineCalculator"));
const PoolFiltrationCalculator = lazy(() => import("./pages/PoolFiltrationCalculator"));
const PoolHeatPumpCalculator = lazy(() => import("./pages/PoolHeatPumpCalculator"));
const PoolSolarCalculator = lazy(() => import("./pages/PoolSolarCalculator"));

// Sprint 154 — Slugs per idioma. Les rutes prefixades de les calculadores es
// generen des de la font unica; abans hi havia vuit linies escrites a ma amb
// el mateix slug per a les sis llengues.
import { ROUTE_SLUGS, pathFor } from "./config/routeSlugs";

// La clau de routeSlugs -> la pagina que la serveix.
const CALC_PAGE = {
  volume: <VolumeCalculator />,
  energy: <EnergyCalculator />,
  water: <WaterCalculator />,
  salt: <PoolSaltCalculator />,
  chlorine: <PoolChlorineCalculator />,
  filtration: <PoolFiltrationCalculator />,
  heatpump: <PoolHeatPumpCalculator />,
  solar: <PoolSolarCalculator />,
};
const ICMCalculator = lazy(() => import("./pages/ICMCalculator"));
const TerraceLoadCalculator = lazy(() => import("./pages/TerraceLoadCalculator"));
const PoolBaseCalculator = lazy(() => import("./pages/PoolBaseCalculator"));
// 21/10/2026 — Configurador de cubiertas (docs/specs/configurador-cubiertas-piscina.md)
const CoverConfigurator = lazy(() => import("./pages/CoverConfigurator"));
const IcmMapPage = lazy(() => import("./pages/IcmMapPage"));

// Installer flows
const InstallerLogin = lazy(() => import("./pages/InstallerLogin"));
const InstallerDashboard = lazy(() => import("./pages/InstallerDashboard"));
const InstallerSetupPassword = lazy(() => import("./pages/InstallerSetupPassword"));

// Sprint 72 — Fullscreen document editor + template management pages
// El fitxer DocumentEditorPage exporta 2 pàgines named. Webpack comparteix
// un únic chunk entre els dos lazy() que apunten al mateix mòdul.
const ProjectDocumentEditorPage = lazy(() =>
  import("./pages/DocumentEditorPage").then((m) => ({ default: m.ProjectDocumentEditorPage }))
);
const TemplateEditorPage = lazy(() =>
  import("./pages/DocumentEditorPage").then((m) => ({ default: m.TemplateEditorPage }))
);
const TemplatesPage = lazy(() => import("./pages/TemplatesPage"));

// Sprint 84 — Pool detector (illegal pools per municipality)
const PoolDetectorPage = lazy(() => import("./pages/PoolDetectorPage"));
const PoolDetectorMapPage = lazy(() => import("./pages/PoolDetectorMapPage"));

// Sprint SEO Blog v1 (2026-08-01) — Blog SEO
const BlogIndex = lazy(() => import("./pages/blog/BlogIndex"));
const BlogArticle = lazy(() => import("./pages/blog/BlogArticle"));
import { 
  Waves, 
  Calculator, 
  Shield, 
  Download, 
  CheckCircle2, 
  ArrowRight, 
  ArrowLeft,
  FileText,
  Droplet,
  Settings,
  Lock,
  MapPin,
  Search,
  Upload,
  Building2,
  User
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Redirect component for old passport URLs to new ficha URLs
const RedirectToFicha = () => {
  const { poollex_id } = useParams();
  return <Navigate to={`/ficha/${poollex_id}`} replace />;
};

// Landing Page - Technical Infrastructure
const LandingPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    empresa: '',
    email: '',
    tipo_organizacion: '',
    pais: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const scrollToForm = () => {
    document.getElementById('form-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToPassport = () => {
    document.getElementById('passport-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/access-requests`, {
        nombre: formData.nombre,
        empresa: formData.empresa,
        email: formData.email,
        tipo_organizacion: formData.tipo_organizacion,
        pais: formData.pais,
      });
      navigate('/thank-you');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar solicitud');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <img src="/poollex-logo.png" alt="PoolLex" className="h-8 w-auto" />
            </div>
            <div className="flex items-center gap-4">
              <span className="hidden sm:inline text-xs font-mono tracking-widest text-slate-400 uppercase">{t('header.initiative')}</span>
              <LanguageSelector variant="ghost" />
            </div>
          </div>
        </div>
      </header>

      {/* 1. HERO */}
      <section className="relative py-24 md:py-36 px-4 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:14px_24px]">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-7">
              <img 
                src="/poollex-logo-full.png" 
                alt="PoolLex.io" 
                className="h-20 md:h-28 w-auto mb-6"
                data-testid="hero-logo"
              />
              <p className="text-xl md:text-2xl font-medium text-slate-700 mb-6">
                {t('landing.heroSubtitle')}
              </p>
              <div className="space-y-4 mb-10">
                <p className="text-base md:text-lg leading-relaxed text-slate-600">
                  {t('landing.heroText1')}
                </p>
                <p className="text-base md:text-lg leading-relaxed text-slate-600">
                  {t('landing.heroText2')}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={scrollToPassport}
                  className="bg-slate-900 text-white hover:bg-slate-800 rounded-none px-8 py-3 font-medium transition-all text-sm tracking-wide"
                  data-testid="cta-explore"
                >
                  {t('landing.ctaExplore')}
                </button>
                <button
                  onClick={scrollToForm}
                  className="bg-transparent border border-slate-300 text-slate-900 hover:bg-slate-50 rounded-none px-8 py-3 font-medium transition-all text-sm tracking-wide"
                  data-testid="cta-access"
                >
                  {t('landing.ctaAccess')}
                </button>
              </div>
            </div>
            <div className="md:col-span-5 hidden md:block">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/cf3b3d29-820c-431a-a6df-a3c37b07f6d9/images/7332b3c5064272a4d84428356c12de57ec26e94edccbf9baad920ce2dc39fc4e.png"
                alt="PoolLex concept diagram"
                className="w-full opacity-80"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 2. PROBLEMA DEL SECTOR */}
      <section className="py-24 md:py-32 px-4 bg-slate-900 text-white relative">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-8">{t('landing.diagLabel')}</p>
          <h2 className="font-outfit text-3xl md:text-5xl font-semibold tracking-tight text-white mb-10">
            {t('landing.diagTitle')}
          </h2>
          <p className="text-lg text-slate-300 leading-relaxed mb-8">
            {t('landing.diagIntro')}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-10">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="bg-white/5 border border-white/10 p-4">
                <p className="text-sm text-slate-300 font-mono">{t(`landing.diagItem${i}`)}</p>
              </div>
            ))}
          </div>
          <p className="text-slate-400 text-base leading-relaxed">
            {t('landing.diagConclusion1')}
          </p>
          <div className="h-px bg-slate-700 w-24 my-8" />
          <p className="text-white font-medium text-lg">
            {t('landing.diagConclusion2')}
          </p>
        </div>
      </section>

      {/* 3. CONCEPTO CENTRAL */}
      <section id="passport-section" className="py-24 md:py-32 px-4 bg-white overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-7">
              <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-8">{t('landing.passportLabel')}</p>
              <h2 className="font-outfit text-3xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-8">
                {t('landing.passportTitle')}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                {t('landing.passportText1')}
              </p>
              <p className="text-base text-slate-500 leading-relaxed">
                {t('landing.passportText2')}
              </p>
            </div>
            <div className="md:col-span-5">
              <div className="aspect-[4/3] overflow-hidden relative">
                <img src="/pool-abstract-1.jpg" alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-white/40 to-transparent" />
                <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 border border-slate-200">
                  <span className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">{t('landing.passportImgLabel')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. LOS 4 PILARES */}
      <section className="py-24 md:py-32 px-4 bg-[#F1F5F9]">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-4 text-center">{t('landing.pillarsLabel')}</p>
          <h2 className="font-outfit text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-16 text-center">
            {t('landing.pillarsTitle')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1,2,3,4].map(i => (
              <div key={i} className="bg-white p-8 border border-slate-100 shadow-sm hover:border-slate-300 transition-colors group relative overflow-hidden" data-testid={`pillar-${i}`}>
                <div className="w-10 h-10 border-2 border-slate-900 flex items-center justify-center mb-6">
                  <span className="font-mono text-sm font-bold text-slate-900">{String(i).padStart(2, '0')}</span>
                </div>
                <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-4">{t(`landing.pillar${i}Title`)}</h3>
                <ul className="space-y-2">
                  {[1,2,3].map(j => (
                    <li key={j} className="text-sm text-slate-600 flex items-start gap-2">
                      <span className="w-1 h-1 bg-slate-400 rounded-full mt-2 shrink-0" />
                      {t(`landing.pillar${i}Item${j}`)}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. ORIGEN */}
      <section className="py-24 md:py-32 px-4 bg-white overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-5 order-2 md:order-1">
              <div className="aspect-[4/3] overflow-hidden relative">
                <img src="/pool-abstract-2.jpg" alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-white/40 to-transparent" />
                <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 border border-slate-200">
                  <span className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">{t('landing.originImgLabel')}</span>
                </div>
              </div>
            </div>
            <div className="md:col-span-7 order-1 md:order-2">
              <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-8">{t('landing.originLabel')}</p>
              <h2 className="font-outfit text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-8">
                {t('landing.originTitle')}
              </h2>
              <div className="space-y-6 text-lg text-slate-600 leading-relaxed">
                <p>
                  {t('landing.originText1')}{' '}
                  <a href="https://legalizatupiscina.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline-offset-4 hover:underline font-medium">Legalizatupiscina.com</a>.
                </p>
                <p>{t('landing.originText2')}</p>
                <p className="font-medium text-slate-800">{t('landing.originText3')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. VISIÓN DE FUTURO */}
      <section className="py-24 md:py-32 px-4 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src="/pool-aerial-city.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/50 to-slate-900/90" />
        </div>
        <div className="max-w-4xl mx-auto relative z-10">
          <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-12">{t('landing.visionLabel')}</p>
          <blockquote className="mb-12">
            <p className="font-outfit text-3xl md:text-5xl font-semibold tracking-tight text-white leading-tight">
              {t('landing.visionTitle1')}
            </p>
            <p className="font-outfit text-3xl md:text-5xl font-semibold tracking-tight text-slate-400 leading-tight mt-2">
              {t('landing.visionTitle2')}
            </p>
          </blockquote>
          <div className="h-px bg-slate-700 w-24 mb-8" />
          <p className="text-slate-400 text-base leading-relaxed max-w-3xl">
            {t('landing.visionText1')}
          </p>
          <p className="text-slate-300 font-medium text-base mt-4">
            {t('landing.visionText2')}
          </p>
        </div>
      </section>

      {/* 7. QUIÉN DEBERÍA INTERESARSE */}
      <section className="py-24 md:py-32 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-4 text-center">{t('landing.targetLabel')}</p>
          <h2 className="font-outfit text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-16 text-center">
            {t('landing.targetTitle')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1,2,3,4].map(i => (
              <div key={i} className="bg-slate-50 p-6 border border-dashed border-slate-300" data-testid={`target-${i}`}>
                <span className="font-mono text-xs text-blue-600 mb-3 block">{String(i).padStart(2, '0')}</span>
                <h3 className="font-outfit font-semibold text-slate-900 mb-2">{t(`landing.target${i}Title`)}</h3>
                <p className="text-sm text-slate-500">{t(`landing.target${i}Desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. CALL TO ACTION + FORM */}
      <section id="form-section" className="py-24 md:py-32 px-4 bg-[#F1F5F9]">
        <div className="max-w-xl mx-auto">
          <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-4 text-center">{t('landing.formLabel')}</p>
          <h2 className="font-outfit text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-4 text-center">
            {t('landing.formTitle')}
          </h2>
          <p className="text-slate-600 text-center mb-4 text-base leading-relaxed">
            {t('landing.formText1')}
          </p>
          <p className="text-slate-500 text-center mb-10 text-sm">
            {t('landing.formText2')}
          </p>
          
          <div className="bg-white p-8 border border-slate-200 shadow-sm">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">{t('landing.formName')} *</label>
                <input name="nombre" value={formData.nombre} onChange={handleChange} required
                  className="w-full rounded-none border border-slate-200 bg-white px-3 py-2.5 text-sm focus:ring-1 focus:ring-slate-900 focus:border-slate-900 focus:outline-none" data-testid="form-nombre" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">{t('landing.formCompany')}</label>
                <input name="empresa" value={formData.empresa} onChange={handleChange}
                  className="w-full rounded-none border border-slate-200 bg-white px-3 py-2.5 text-sm focus:ring-1 focus:ring-slate-900 focus:border-slate-900 focus:outline-none" data-testid="form-empresa" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">{t('landing.formEmail')} *</label>
                <input name="email" type="email" value={formData.email} onChange={handleChange} required
                  className="w-full rounded-none border border-slate-200 bg-white px-3 py-2.5 text-sm focus:ring-1 focus:ring-slate-900 focus:border-slate-900 focus:outline-none" data-testid="form-email" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">{t('landing.formOrgType')}</label>
                <Select onValueChange={(v) => handleSelectChange('tipo_organizacion', v)}>
                  <SelectTrigger className="rounded-none border-slate-200" data-testid="form-tipo-org">
                    <SelectValue placeholder={t('landing.formOrgSelect')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="distribuidor">{t('landing.formOrgDistributor')}</SelectItem>
                    <SelectItem value="fabricante">{t('landing.formOrgManufacturer')}</SelectItem>
                    <SelectItem value="tecnico">{t('landing.formOrgTechnician')}</SelectItem>
                    <SelectItem value="administracion">{t('landing.formOrgPublic')}</SelectItem>
                    <SelectItem value="otro">{t('landing.formOrgOther')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">{t('landing.formCountry')}</label>
                <input name="pais" value={formData.pais} onChange={handleChange}
                  className="w-full rounded-none border border-slate-200 bg-white px-3 py-2.5 text-sm focus:ring-1 focus:ring-slate-900 focus:border-slate-900 focus:outline-none" data-testid="form-pais" />
              </div>
              <button type="submit"
                className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-none px-8 py-3 font-medium transition-all text-sm tracking-wide disabled:opacity-50"
                disabled={loading} data-testid="form-submit">
                {loading ? t('landing.formSubmitting') : t('landing.formSubmit')}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* 9. FOOTER */}
      <footer className="py-12 px-4 bg-slate-900 border-t border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
            <div>
              <p className="text-white font-outfit font-semibold text-lg mb-1">PoolLex</p>
              <p className="text-slate-500 text-sm">
                {t('footer.poweredBy')}{' '}
                <a href="https://legalizatupiscina.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline-offset-4 hover:underline">
                  Legalizatupiscina
                </a>
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8">
              <a href="https://legalizatupiscina.com" target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 hover:text-white transition-colors">
                Legalizatupiscina.com
              </a>
              <a href="mailto:info@legalizatupiscina.com" className="text-sm text-slate-400 hover:text-white transition-colors">
                {t('footer.contact')}
              </a>
              <a href="/privacy" className="text-sm text-slate-400 hover:text-white transition-colors">
                {t('footer.privacy')}
              </a>
              <a href="/legal" className="text-sm text-slate-400 hover:text-white transition-colors">
                {t('footer.legal')}
              </a>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-slate-800">
            <p className="text-xs font-mono text-slate-600 text-center">
              {t('footer.initiative')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Thank You Page
const ThankYouPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  return (
    <div className="min-h-screen bg-[#FAFBFC] flex items-center justify-center px-4">
      <div className="max-w-md text-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 className="w-10 h-10 text-green-600" />
        </div>
        <h1 className="font-outfit text-3xl font-bold text-slate-900 mb-4">
          {t('thankYou.title')}
        </h1>
        <p className="text-slate-600 mb-8">
          {t('thankYou.message')}
        </p>
        <Button onClick={() => navigate('/')} variant="outline">
          {t('thankYou.back')}
        </Button>
      </div>
    </div>
  );
};

// Privacy Page
const PrivacyPage = () => {
  const { t } = useTranslation();
  
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-4 py-16">
        {/* 12/10/2026 — Aquests dos t() no tenien text de reserva i les
            namespaces `privacy` i `cookies` NO existeixen a cap dels sis
            JSON d'idioma. Resultat: l'h1 de la política de privacitat deia
            literalment «privacy.title» i la data, «privacy.lastUpdated:
            Diciembre 2025». Trobat posant la pàgina a noindex.

            La reserva va en castellà i no s'afegeixen claus noves perquè el
            COS d'aquesta pàgina és castellà fix: traduir-ne només el títol
            donaria una pàgina amb capçalera en neerlandès i text en
            castellà. És el mateix patró que ja fa servir LegalPage.js. */}
        {/* La data d'aquesta pàgina viu en TRES llocs que s'han de moure
            junts: aquí, a CookiesPage (just a sota) i a `legal.updateDate`
            dels sis JSON d'idioma, que és d'on la llegeix LegalPage.js. Si
            es canvia una i no les altres, el lloc diu dues dates de revisió
            diferents per als mateixos termes. */}
        <h1 className="font-outfit text-3xl font-bold text-slate-900 mb-8">{t('privacy.title', 'Política de Privacidad')}</h1>
        <p className="text-sm text-slate-500 mb-8">{t('privacy.lastUpdated', 'Última actualización')}: Octubre 2026</p>
        <div className="prose prose-slate max-w-none">
          <h2>1. Responsable del tratamiento</h2>
          <p>El responsable del tratamiento de los datos personales es PoolLex / LegalizaTuPiscina.</p>
          
          <h2>2. Datos que recopilamos</h2>
          <p>Recopilamos los datos que nos proporcionas voluntariamente a través de nuestro formulario de contacto: nombre de empresa, email, teléfono, provincia y rol profesional.</p>
          
          <h2>3. Finalidad del tratamiento</h2>
          <p>Los datos se utilizan exclusivamente para gestionar las solicitudes de acceso a la plataforma PoolLex y comunicarnos contigo sobre el estado de tu solicitud.</p>
          
          <h2>4. Base legal</h2>
          <p>El tratamiento de tus datos se basa en tu consentimiento expreso al enviar el formulario de solicitud.</p>
          
          <h2>5. Conservación de datos</h2>
          <p>Los datos se conservarán mientras exista una relación comercial o durante el tiempo necesario para cumplir con las obligaciones legales.</p>
          
          <h2>6. Derechos del usuario</h2>
          <p>Puedes ejercer tus derechos de acceso, rectificación, supresión, limitación, portabilidad y oposición enviando un email a info@legalizatupiscina.com.</p>
          
          <h2>7. Contacto</h2>
          <p>Para cualquier consulta relacionada con la privacidad: info@legalizatupiscina.com</p>
        </div>
        <div className="mt-12">
          <a href="/" className="text-primary hover:underline">← Volver al inicio</a>
        </div>
      </div>
    </div>
  );
};

// Cookies Page
// Sprint 119.111b — noindex,follow: pàgina legal generica que Google no vol
// indexar (baix EAT). Al posar noindex, GSC treu la URL del bucket "Descubierta
// actualmente sin indexar" — resol 1/18 URLs GSC report 17/8/26.
const CookiesPage = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <meta name="robots" content="noindex,follow" />
      </Helmet>
      <div className="max-w-3xl mx-auto px-4 py-16">
        {/* Mateix cas que a PrivacyPage: cap text de reserva i la namespace
            `cookies` no existeix. L'h1 deia «cookies.title». */}
        <h1 className="font-outfit text-3xl font-bold text-slate-900 mb-8">{t('cookies.title', 'Política de Cookies')}</h1>
        <p className="text-sm text-slate-500 mb-8">{t('cookies.lastUpdated', 'Última actualización')}: Octubre 2026</p>
        <div className="prose prose-slate max-w-none">
          <h2>1. ¿Qué son las cookies?</h2>
          <p>Las cookies son pequeños archivos de texto que se almacenan en tu dispositivo cuando visitas un sitio web.</p>
          
          <h2>2. Cookies que utilizamos</h2>
          <h3>Cookies técnicas (necesarias)</h3>
          <p>Son esenciales para el funcionamiento del sitio y no pueden ser desactivadas. Incluyen cookies de sesión y preferencia de idioma.</p>
          
          <h3>Cookies analíticas</h3>
          <p>Utilizamos PostHog para analizar el uso del sitio de forma anónima y mejorar nuestros servicios.</p>
          
          <h2>3. Cómo gestionar las cookies</h2>
          <p>Puedes configurar tu navegador para bloquear o eliminar cookies. Ten en cuenta que esto puede afectar la funcionalidad del sitio.</p>
          
          <h2>4. Actualizaciones</h2>
          <p>Esta política puede actualizarse periódicamente. Te recomendamos revisarla regularmente.</p>
          
          <h2>5. Contacto</h2>
          <p>Para consultas sobre cookies: info@legalizatupiscina.com</p>
        </div>
        <div className="mt-12">
          <a href="/" className="text-primary hover:underline">← Volver al inicio</a>
        </div>
      </div>
    </div>
  );
};

// Form Wizard Page
const FormWizard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const serviceType = location.state?.serviceType || 'sin_firma';
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    service_type: serviceType,
    
    // Paso 1: Cliente/Titular
    cliente_nombre: '',
    cliente_dni: '',
    cliente_email: '',
    cliente_telefono: '',
    cliente_direccion: '',
    cliente_cp: '',
    cliente_municipio: '',
    cliente_provincia: '',
    
    // Paso 2: Promotor (puede ser igual al cliente)
    promotor_igual_cliente: true,
    promotor_nombre: '',
    promotor_dni: '',
    promotor_rol: '',
    promotor_direccion: '',
    promotor_cp: '',
    promotor_municipio: '',
    promotor_provincia: '',
    promotor_telefono: '',
    promotor_email: '',
    
    // Paso 3: Ubicación Obra
    obra_direccion: '',
    obra_cp: '',
    obra_municipio: '',
    obra_provincia: '',
    referencia_catastral: '',
    latitude: null,
    longitude: null,
    pool_rotation: 0,
    map_image: null,
    
    // Paso 4: Dimensiones Piscina
    piscina_length: '',
    piscina_width: '',
    piscina_prof_min: '',
    piscina_prof_max: '',
    
    // Paso 5: Características Técnicas
    piscina_tipo: '',
    piscina_modelo: '',
    piscina_material_construccion: '',
    piscina_revestimiento: '',
    
    // Paso 6: Filtración
    filtration_type: '',
    filtration_capacity: '',
    pump_power: '',
    metodo_desinfeccion: '',
    control_ph_cloro: '',
    
    // Paso 7: Seguridad
    has_cover: false,
    has_fence: false,
    has_alarm: false,
    vallado: '',
    cubierta_seguridad: '',
    accesos_piscina: '',
    senalizacion_prof: '',
    dispositivos_seguridad: '',
    
    // Paso 8: Empresa Instaladora
    instaladora_nombre: '',
    instaladora_cif: '',
    instaladora_direccion: '',
    instaladora_cp: '',
    instaladora_municipio: '',
    instaladora_provincia: '',
    instaladora_telefono: '',
    instaladora_email: '',
    
    // Paso 9: Presupuesto y Firma
    presupuesto_pem: '',
    lugar_firma: '',
    fecha_firma: '',
    
    // Paso 10: Fotos (solo CON FIRMA)
    fotos_piscina: []
  });

  const [addressSearch, setAddressSearch] = useState('');
  const [searchingAddress, setSearchingAddress] = useState(false);
  const [validatingCatastral, setValidatingCatastral] = useState(false);

  // Total steps según servicio (con fotos = 10, sin fotos = 9)
  const totalSteps = serviceType === 'con_firma' ? 10 : 9;
  const progress = (step / totalSteps) * 100;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddressSearch = async () => {
    if (!addressSearch.trim()) {
      toast.error('Por favor introduce una dirección para buscar');
      return;
    }

    setSearchingAddress(true);
    try {
      const response = await axios.get(`${API}/geocode`, {
        params: { address: addressSearch }
      });

      if (response.data.success) {
        setFormData(prev => ({
          ...prev,
          project_location: response.data.display_name,
          latitude: response.data.latitude,
          longitude: response.data.longitude
        }));
        toast.success('✓ Ubicación encontrada y marcada en el mapa');
      }
    } catch (error) {
      console.error('Error geocoding:', error);
      toast.error('No se encontró la dirección. Puedes clickear directamente en el mapa.');
    } finally {
      setSearchingAddress(false);
    }
  };

  const handleValidateCatastral = async () => {
    if (!formData.referencia_catastral) {
      toast.error('Por favor introduce una referencia catastral');
      return;
    }

    setValidatingCatastral(true);
    try {
      const response = await axios.post(`${API}/validate-catastral`, {
        referencia_catastral: formData.referencia_catastral
      });

      if (response.data.valido) {
        toast.success('Referencia catastral válida');
        
        // Si devuelve coordenadas, actualizar
        if (response.data.coordenadas) {
          setFormData(prev => ({
            ...prev,
            latitude: response.data.coordenadas.latitud,
            longitude: response.data.coordenadas.longitud
          }));
        }
      }
    } catch (error) {
      console.error('Error validating catastral:', error);
      toast.error(error.response?.data?.detail || 'Referencia catastral inválida');
    } finally {
      setValidatingCatastral(false);
    }
  };

  const handleMapLocationSelect = async (location) => {
    setFormData(prev => ({
      ...prev,
      latitude: location.latitude,
      longitude: location.longitude
    }));
    toast.success('✓ Ubicación seleccionada en el mapa');
    
    // Consultar referencia catastral automáticamente
    try {
      const response = await axios.get(`${API}/catastral-from-coords`, {
        params: {
          lat: location.latitude,
          lon: location.longitude
        }
      });
      
      if (response.data.success && response.data.referencia_catastral) {
        setFormData(prev => ({
          ...prev,
          referencia_catastral: response.data.referencia_catastral
        }));
        
        // Si también devuelve dirección, actualizarla
        if (response.data.direccion) {
          setFormData(prev => ({
            ...prev,
            obra_direccion: response.data.direccion
          }));
        }
        
        toast.success('✓ Referencia catastral obtenida automáticamente');
      } else {
        // No mostrar nada, el servicio del Catastro es inestable
        console.log('No se pudo obtener referencia catastral automáticamente');
      }
    } catch (error) {
      console.error('Error obteniendo referencia catastral:', error);
      // Silenciar error, el usuario puede introducirla manualmente
    }
  };

  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Mapear campos del formulario al modelo del backend
      const payload = {
        service_type: formData.service_type,
        // Dimensiones piscina
        length: parseFloat(formData.piscina_length),
        width: parseFloat(formData.piscina_width),
        shallow_depth: parseFloat(formData.piscina_prof_min),
        deep_depth: parseFloat(formData.piscina_prof_max),
        filtration_type: formData.filtration_type,
        pump_power: formData.pump_power ? parseFloat(formData.pump_power) : null,
        // Seguridad
        has_cover: formData.has_cover || false,
        has_fence: formData.has_fence || false,
        has_alarm: formData.has_alarm || false,
        // Cliente
        client_name: formData.cliente_nombre,
        client_dni: formData.cliente_dni,
        client_email: formData.cliente_email,
        client_phone: formData.cliente_telefono || '',
        // Ubicación
        project_location: formData.obra_direccion,
        referencia_catastral: formData.referencia_catastral,
        latitude: formData.latitude,
        longitude: formData.longitude,
        // Datos adicionales (se guardan en pool_data)
        extra_data: {
          cliente_cp: formData.cliente_cp,
          cliente_municipio: formData.cliente_municipio,
          cliente_provincia: formData.cliente_provincia,
          promotor_igual_cliente: formData.promotor_igual_cliente,
          promotor_nombre: formData.promotor_nombre,
          promotor_dni: formData.promotor_dni,
          obra_cp: formData.obra_cp,
          obra_municipio: formData.obra_municipio,
          obra_provincia: formData.obra_provincia,
          piscina_tipo: formData.piscina_tipo,
          piscina_modelo: formData.piscina_modelo,
          piscina_material_construccion: formData.piscina_material_construccion,
          piscina_revestimiento: formData.piscina_revestimiento,
          metodo_desinfeccion: formData.metodo_desinfeccion,
          control_ph_cloro: formData.control_ph_cloro,
          filtration_capacity: formData.filtration_capacity,
          vallado: formData.vallado,
          cubierta_seguridad: formData.cubierta_seguridad,
          accesos_piscina: formData.accesos_piscina,
          senalizacion_prof: formData.senalizacion_prof,
          dispositivos_seguridad: formData.dispositivos_seguridad,
          instaladora_nombre: formData.instaladora_nombre,
          instaladora_cif: formData.instaladora_cif,
          instaladora_direccion: formData.instaladora_direccion,
          instaladora_cp: formData.instaladora_cp,
          instaladora_municipio: formData.instaladora_municipio,
          instaladora_provincia: formData.instaladora_provincia,
          instaladora_telefono: formData.instaladora_telefono,
          instaladora_email: formData.instaladora_email,
          presupuesto_pem: formData.presupuesto_pem,
          lugar_firma: formData.lugar_firma,
          fecha_firma: formData.fecha_firma,
          pool_rotation: formData.pool_rotation || 0,
          map_image: formData.map_image || null
        }
      };

      // Configurar headers si el cliente está autenticado
      const clientToken = localStorage.getItem('client_token');
      const config = clientToken ? {
        headers: { Authorization: `Bearer ${clientToken}` }
      } : {};

      const response = await axios.post(`${API}/projects/calculate`, payload, config);
      const projectId = response.data.project_id;
      
      // Si es CON FIRMA y hay fotos, subirlas
      if (serviceType === 'con_firma' && formData.fotos_piscina && formData.fotos_piscina.length > 0) {
        try {
          const photoFormData = new FormData();
          for (const file of formData.fotos_piscina) {
            photoFormData.append('files', file);
          }
          
          await axios.post(`${API}/projects/${projectId}/photos`, photoFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          
          toast.success(`${formData.fotos_piscina.length} foto(s) subida(s) correctamente`);
        } catch (photoError) {
          console.error('Error subiendo fotos:', photoError);
          toast.error('Proyecto creado, pero hubo un error subiendo las fotos. Puedes subirlas después.');
        }
      }
      
      // Si se usó crédito, ir directo al dashboard
      if (response.data.used_credit) {
        toast.success(`¡Proyecto ${response.data.project_number} creado! Se ha usado 1 crédito. Te quedan ${response.data.remaining_credits} créditos.`);
        navigate('/cliente/dashboard');
      } else {
        // Si no hay créditos, ir al flujo normal de pago
        toast.success(`¡Proyecto ${response.data.project_number || ''} creado! Procede al pago para continuar.`);
        navigate(`/preview/${response.data.project_id}`);
      }
    } catch (error) {
      console.error('Error creating project:', error);
      console.error('Error response:', error.response?.data);
      const errorMsg = error.response?.data?.detail || 'Error al crear el proyecto. Por favor, inténtalo de nuevo.';
      toast.error(errorMsg);
      // No navegar a ningún lado en caso de error, quedarse en el formulario
    } finally {
      setLoading(false);
    }
  };

  const isStepValid = () => {
    switch (step) {
      case 1: // Datos Cliente
        return formData.cliente_nombre && formData.cliente_dni && formData.cliente_email && 
               formData.cliente_direccion && formData.cliente_cp && formData.cliente_municipio;
      case 2: // Datos Promotor
        return formData.promotor_igual_cliente || 
               (formData.promotor_nombre && formData.promotor_dni && formData.promotor_rol);
      case 3: // Ubicación Obra + Dimensiones
        return formData.obra_direccion && formData.referencia_catastral && 
               formData.latitude && formData.longitude &&
               formData.piscina_length && formData.piscina_width && 
               formData.piscina_prof_min && formData.piscina_prof_max;
      case 4: // Confirmación (auto-valid si paso 3 está completo)
        return formData.latitude && formData.longitude;
      case 5: // Características Técnicas
        return formData.piscina_tipo && formData.piscina_material_construccion;
      case 6: // Filtración
        return formData.filtration_type && formData.metodo_desinfeccion;
      case 7: // Seguridad
        return true; // Todos opcionales
      case 8: // Instaladora
        return formData.instaladora_nombre && formData.instaladora_cif;
      case 9: // Presupuesto
        return formData.presupuesto_pem;
      case 10: // Fotos (solo CON FIRMA)
        return true; // Validación de fotos se hará después
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen bg-[#F1F7FD] py-12">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8 text-center">
          <button 
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-primary hover:text-primary-hover mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio
          </button>
          <h1 className="font-outfit text-4xl font-bold text-slate-900 mb-2">
            Crear Dossier Técnico
          </h1>
          <p className="text-slate-600">Paso {step} de {totalSteps}</p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <Progress value={progress} className="h-2" />
          <div className="grid grid-cols-5 gap-2 mt-2 text-xs text-slate-600">
            <span className={step >= 1 ? 'text-primary font-semibold' : ''}>Cliente</span>
            <span className={step >= 2 ? 'text-primary font-semibold' : ''}>Promotor</span>
            <span className={step >= 3 ? 'text-primary font-semibold' : ''}>Ubicación</span>
            <span className={step >= 4 ? 'text-primary font-semibold' : ''}>Dimensiones</span>
            <span className={step >= 5 ? 'text-primary font-semibold' : ''}>Técnico</span>
          </div>
          {totalSteps > 5 && (
            <div className="grid grid-cols-5 gap-2 mt-1 text-xs text-slate-600">
              <span className={step >= 6 ? 'text-primary font-semibold' : ''}>Filtración</span>
              <span className={step >= 7 ? 'text-primary font-semibold' : ''}>Seguridad</span>
              <span className={step >= 8 ? 'text-primary font-semibold' : ''}>Instaladora</span>
              <span className={step >= 9 ? 'text-primary font-semibold' : ''}>Presupuesto</span>
              {serviceType === 'con_firma' && <span className={step >= 10 ? 'text-primary font-semibold' : ''}>Fotos</span>}
            </div>
          )}
        </div>

        {/* Form Card */}
        <Card className="glass-card shadow-xl border-slate-200">
          <CardHeader>
            <CardTitle className="font-outfit text-2xl flex items-center gap-3">
              {step === 1 && <><FileText className="w-6 h-6 text-primary" /> Datos del Cliente/Titular</>}
              {step === 2 && <><FileText className="w-6 h-6 text-primary" /> Datos del Promotor</>}
              {step === 3 && <><MapPin className="w-6 h-6 text-primary" /> Ubicación de la Obra</>}
              {step === 4 && <><CheckCircle2 className="w-6 h-6 text-primary" /> Confirmación Ubicación</>}
              {step === 5 && <><Settings className="w-6 h-6 text-primary" /> Características Técnicas</>}
              {step === 6 && <><Settings className="w-6 h-6 text-primary" /> Sistema de Filtración</>}
              {step === 7 && <><Shield className="w-6 h-6 text-primary" /> Elementos de Seguridad</>}
              {step === 8 && <><Building2 className="w-6 h-6 text-primary" /> Empresa Instaladora</>}
              {step === 9 && <><FileText className="w-6 h-6 text-primary" /> Presupuesto</>}
              {step === 10 && <><Upload className="w-6 h-6 text-primary" /> Fotografías de la Piscina</>}
            </CardTitle>
            <CardDescription>
              {step === 1 && 'Información personal del titular de la piscina'}
              {step === 2 && 'Datos del promotor de la obra (puede ser el mismo que el titular)'}
              {step === 3 && 'Dirección exacta donde se ubica la piscina'}
              {step === 4 && 'Verifica los datos de ubicación y dimensiones'}
              {step === 5 && 'Tipo, modelo y materiales de la piscina'}
              {step === 6 && 'Sistema de filtración y depuración'}
              {step === 7 && 'Elementos de seguridad instalados'}
              {step === 8 && 'Datos de la empresa que realiza la instalación'}
              {step === 9 && 'Presupuesto y datos de firma del proyecto'}
              {step === 10 && 'Sube fotos de la piscina construida'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Step 1: Datos Cliente/Titular */}
            {step === 1 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="cliente_nombre">Nombre Completo *</Label>
                    <Input
                      id="cliente_nombre"
                      data-testid="cliente-nombre-input"
                      value={formData.cliente_nombre}
                      onChange={(e) => handleInputChange('cliente_nombre', e.target.value)}
                      placeholder="Juan García López"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cliente_dni">DNI/NIE *</Label>
                    <Input
                      id="cliente_dni"
                      data-testid="cliente-dni-input"
                      value={formData.cliente_dni}
                      onChange={(e) => handleInputChange('cliente_dni', e.target.value.toUpperCase())}
                      placeholder="12345678A"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="cliente_email">Email *</Label>
                    <Input
                      id="cliente_email"
                      data-testid="cliente-email-input"
                      type="email"
                      value={formData.cliente_email}
                      onChange={(e) => handleInputChange('cliente_email', e.target.value)}
                      placeholder="correo@ejemplo.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cliente_telefono">Teléfono</Label>
                    <Input
                      id="cliente_telefono"
                      data-testid="cliente-telefono-input"
                      value={formData.cliente_telefono}
                      onChange={(e) => handleInputChange('cliente_telefono', e.target.value)}
                      placeholder="600123456"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="cliente_direccion">Dirección Completa *</Label>
                  <Input
                    id="cliente_direccion"
                    data-testid="cliente-direccion-input"
                    value={formData.cliente_direccion}
                    onChange={(e) => handleInputChange('cliente_direccion', e.target.value)}
                    placeholder="Calle Mayor, 1"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="cliente_cp">Código Postal *</Label>
                    <Input
                      id="cliente_cp"
                      data-testid="cliente-cp-input"
                      value={formData.cliente_cp}
                      onChange={(e) => handleInputChange('cliente_cp', e.target.value)}
                      placeholder="28001"
                      maxLength={5}
                    />
                  </div>
                  <div>
                    <Label htmlFor="cliente_municipio">Municipio *</Label>
                    <Input
                      id="cliente_municipio"
                      data-testid="cliente-municipio-input"
                      value={formData.cliente_municipio}
                      onChange={(e) => handleInputChange('cliente_municipio', e.target.value)}
                      placeholder="Madrid"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cliente_provincia">Provincia</Label>
                    <Input
                      id="cliente_provincia"
                      data-testid="cliente-provincia-input"
                      value={formData.cliente_provincia}
                      onChange={(e) => handleInputChange('cliente_provincia', e.target.value)}
                      placeholder="Madrid"
                    />
                  </div>
                </div>

                {/* Checkbox Promotor igual Cliente */}
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <div className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <Checkbox
                      id="promotor_igual_cliente"
                      data-testid="promotor-igual-cliente-checkbox"
                      checked={formData.promotor_igual_cliente}
                      onCheckedChange={(checked) => handleInputChange('promotor_igual_cliente', checked)}
                    />
                    <Label htmlFor="promotor_igual_cliente" className="cursor-pointer">
                      <div className="font-semibold text-slate-900">El promotor es el mismo que el titular</div>
                      <div className="text-xs text-slate-600">Marca esta casilla si eres tú quien promueve la obra</div>
                    </Label>
                  </div>
                </div>
              </div>
            )}

            {/* PASO 1 ANTIGUO - Dimensions - AHORA ES PASO 4 */}
            {step === 4 && (
              <div className="space-y-6">
                {/* Resumen de ubicación y dimensiones */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-green-900 mb-3">✓ Resumen de Ubicación y Piscina</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-slate-600">Ubicación:</p>
                      <p className="font-medium">{formData.obra_direccion}</p>
                      <p className="text-xs text-slate-500">{formData.obra_cp} {formData.obra_municipio}</p>
                    </div>
                    <div>
                      <p className="text-slate-600">Ref. Catastral:</p>
                      <p className="font-medium font-mono">{formData.referencia_catastral}</p>
                    </div>
                    <div>
                      <p className="text-slate-600">Dimensiones:</p>
                      <p className="font-medium">{formData.piscina_length}m × {formData.piscina_width}m</p>
                    </div>
                    <div>
                      <p className="text-slate-600">Profundidad:</p>
                      <p className="font-medium">{formData.piscina_prof_min}m - {formData.piscina_prof_max}m</p>
                    </div>
                    <div>
                      <p className="text-slate-600">Coordenadas:</p>
                      <p className="font-medium font-mono text-xs">
                        {formData.latitude?.toFixed(6)}, {formData.longitude?.toFixed(6)}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-600">Orientación:</p>
                      <p className="font-medium">{formData.pool_rotation || 0}°</p>
                    </div>
                  </div>
                </div>

                {/* Preview del mapa capturado */}
                {formData.map_image && (
                  <div>
                    <Label className="mb-2 block">Vista previa del emplazamiento</Label>
                    <img 
                      src={formData.map_image} 
                      alt="Mapa de ubicación" 
                      className="w-full rounded-lg border shadow-sm"
                    />
                    <p className="text-xs text-slate-500 mt-1">Esta imagen se incluirá en el PDF del proyecto</p>
                  </div>
                )}

                {!formData.map_image && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
                    ⚠️ No has capturado la imagen del mapa. Vuelve al paso anterior y haz clic en el botón de cámara para capturar la imagen que se incluirá en el PDF.
                  </div>
                )}

                <p className="text-sm text-slate-600">
                  Si los datos son correctos, continúa al siguiente paso. Si necesitas modificar algo, vuelve al paso anterior.
                </p>
              </div>
            )}

            {/* Step 2: Datos Promotor */}
            {step === 2 && (
              <div className="space-y-4">
                {formData.promotor_igual_cliente && (
                  <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                    <p className="text-sm text-green-800">
                      ✓ Los datos del promotor son los mismos que los del titular. Puedes continuar al siguiente paso.
                    </p>
                  </div>
                )}

                {!formData.promotor_igual_cliente && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="promotor_nombre">Nombre del Promotor *</Label>
                        <Input
                          id="promotor_nombre"
                          value={formData.promotor_nombre}
                          onChange={(e) => handleInputChange('promotor_nombre', e.target.value)}
                          placeholder="Nombre completo"
                        />
                      </div>
                      <div>
                        <Label htmlFor="promotor_dni">DNI/CIF *</Label>
                        <Input
                          id="promotor_dni"
                          value={formData.promotor_dni}
                          onChange={(e) => handleInputChange('promotor_dni', e.target.value.toUpperCase())}
                          placeholder="12345678A"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="promotor_rol">Rol/Cargo *</Label>
                      <Input
                        id="promotor_rol"
                        value={formData.promotor_rol}
                        onChange={(e) => handleInputChange('promotor_rol', e.target.value)}
                        placeholder="Propietario, Representante legal, etc."
                      />
                    </div>

                    <div>
                      <Label htmlFor="promotor_direccion">Dirección</Label>
                      <Input
                        id="promotor_direccion"
                        value={formData.promotor_direccion}
                        onChange={(e) => handleInputChange('promotor_direccion', e.target.value)}
                        placeholder="Dirección completa"
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="promotor_cp">CP</Label>
                        <Input
                          id="promotor_cp"
                          value={formData.promotor_cp}
                          onChange={(e) => handleInputChange('promotor_cp', e.target.value)}
                          maxLength={5}
                        />
                      </div>
                      <div>
                        <Label htmlFor="promotor_municipio">Municipio</Label>
                        <Input
                          id="promotor_municipio"
                          value={formData.promotor_municipio}
                          onChange={(e) => handleInputChange('promotor_municipio', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label htmlFor="promotor_provincia">Provincia</Label>
                        <Input
                          id="promotor_provincia"
                          value={formData.promotor_provincia}
                          onChange={(e) => handleInputChange('promotor_provincia', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="promotor_telefono">Teléfono</Label>
                        <Input
                          id="promotor_telefono"
                          value={formData.promotor_telefono}
                          onChange={(e) => handleInputChange('promotor_telefono', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label htmlFor="promotor_email">Email</Label>
                        <Input
                          id="promotor_email"
                          type="email"
                          value={formData.promotor_email}
                          onChange={(e) => handleInputChange('promotor_email', e.target.value)}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Step 5: Características Técnicas */}
            {step === 5 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="piscina_tipo">Tipo de Piscina *</Label>
                    <Select 
                      value={formData.piscina_tipo}
                      onValueChange={(value) => handleInputChange('piscina_tipo', value)}
                    >
                      <SelectTrigger id="piscina_tipo">
                        <SelectValue placeholder="Selecciona tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="enterrada">Enterrada</SelectItem>
                        <SelectItem value="semi-enterrada">Semi-enterrada</SelectItem>
                        <SelectItem value="elevada">Elevada</SelectItem>
                        <SelectItem value="desmontable">Desmontable</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="piscina_modelo">Modelo/Referencia</Label>
                    <Input
                      id="piscina_modelo"
                      value={formData.piscina_modelo}
                      onChange={(e) => handleInputChange('piscina_modelo', e.target.value)}
                      placeholder="Ej: Modelo XYZ-2024"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="piscina_material_construccion">Material de Construcción *</Label>
                    <Select 
                      value={formData.piscina_material_construccion}
                      onValueChange={(value) => handleInputChange('piscina_material_construccion', value)}
                    >
                      <SelectTrigger id="piscina_material_construccion">
                        <SelectValue placeholder="Selecciona material" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hormigon">Hormigón armado</SelectItem>
                        <SelectItem value="hormigon-proyectado">Hormigón proyectado</SelectItem>
                        <SelectItem value="poliester">Poliéster reforzado</SelectItem>
                        <SelectItem value="liner">Liner</SelectItem>
                        <SelectItem value="acero">Acero</SelectItem>
                        <SelectItem value="otro">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="piscina_revestimiento">Revestimiento</Label>
                    <Select 
                      value={formData.piscina_revestimiento}
                      onValueChange={(value) => handleInputChange('piscina_revestimiento', value)}
                    >
                      <SelectTrigger id="piscina_revestimiento">
                        <SelectValue placeholder="Selecciona revestimiento" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gresite">Gresite</SelectItem>
                        <SelectItem value="liner">Liner</SelectItem>
                        <SelectItem value="pintura">Pintura específica</SelectItem>
                        <SelectItem value="poliester">Poliéster</SelectItem>
                        <SelectItem value="ceramica">Cerámica</SelectItem>
                        <SelectItem value="otro">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Step 6: Filtración y Depuración */}
            {step === 6 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="filtration_type">Tipo de Filtración *</Label>
                    <Select 
                      value={formData.filtration_type}
                      onValueChange={(value) => handleInputChange('filtration_type', value)}
                    >
                      <SelectTrigger id="filtration_type" data-testid="filtration-type-select">
                        <SelectValue placeholder="Selecciona tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="arena">Filtro de Arena</SelectItem>
                        <SelectItem value="cartucho">Filtro de Cartucho</SelectItem>
                        <SelectItem value="diatomeas">Filtro de Diatomeas</SelectItem>
                        <SelectItem value="vidrio">Filtro de Vidrio</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="filtration_capacity">Capacidad Filtración (m³/h)</Label>
                    <Input
                      id="filtration_capacity"
                      type="number"
                      step="0.1"
                      value={formData.filtration_capacity}
                      onChange={(e) => handleInputChange('filtration_capacity', e.target.value)}
                      placeholder="Ej: 12.5"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="pump_power">Potencia Bomba (kW)</Label>
                    <Input
                      id="pump_power"
                      data-testid="pump-power-input"
                      type="number"
                      step="0.1"
                      value={formData.pump_power}
                      onChange={(e) => handleInputChange('pump_power', e.target.value)}
                      placeholder="1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="metodo_desinfeccion">Método de Desinfección *</Label>
                    <Select 
                      value={formData.metodo_desinfeccion}
                      onValueChange={(value) => handleInputChange('metodo_desinfeccion', value)}
                    >
                      <SelectTrigger id="metodo_desinfeccion">
                        <SelectValue placeholder="Selecciona método" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cloro">Cloro (hipoclorito)</SelectItem>
                        <SelectItem value="sal">Electrólisis salina</SelectItem>
                        <SelectItem value="bromo">Bromo</SelectItem>
                        <SelectItem value="ozono">Ozono</SelectItem>
                        <SelectItem value="uv">Rayos UV</SelectItem>
                        <SelectItem value="otro">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="control_ph_cloro">Sistema Control pH y Cloro</Label>
                  <Select 
                    value={formData.control_ph_cloro}
                    onValueChange={(value) => handleInputChange('control_ph_cloro', value)}
                  >
                    <SelectTrigger id="control_ph_cloro">
                      <SelectValue placeholder="Selecciona sistema" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Control Manual</SelectItem>
                      <SelectItem value="automatico">Control Automático</SelectItem>
                      <SelectItem value="dosificador">Dosificador Automático</SelectItem>
                      <SelectItem value="ninguno">Sin control</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Step 3: Ubicación de la Obra */}
            {step === 3 && (
              <div className="space-y-6">
                {/* Row 1: Dirección y búsqueda */}
                <div>
                  <Label htmlFor="obra_direccion">Dirección de la Obra *</Label>
                  <Input
                    id="obra_direccion"
                    data-testid="obra-direccion-input"
                    value={formData.obra_direccion}
                    onChange={(e) => handleInputChange('obra_direccion', e.target.value)}
                    className="input-focus"
                    placeholder="Calle/Avenida donde está la piscina"
                  />
                  <div className="flex gap-2 mt-2">
                    <Input
                      data-testid="address-search-input"
                      value={addressSearch}
                      onChange={(e) => setAddressSearch(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddressSearch()}
                      className="input-focus flex-1 text-sm"
                      placeholder="Buscar dirección para ubicarla en el mapa..."
                    />
                    <Button
                      data-testid="search-address-button"
                      onClick={handleAddressSearch}
                      disabled={searchingAddress}
                      size="sm"
                      variant="outline"
                    >
                      <Search className="w-4 h-4 mr-1" />
                      {searchingAddress ? 'Buscando...' : 'Buscar'}
                    </Button>
                  </div>
                </div>

                {/* Row 2: CP, Municipio, Provincia */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="obra_cp">Código Postal *</Label>
                    <Input
                      id="obra_cp"
                      value={formData.obra_cp}
                      onChange={(e) => handleInputChange('obra_cp', e.target.value)}
                      maxLength={5}
                      placeholder="28001"
                    />
                  </div>
                  <div>
                    <Label htmlFor="obra_municipio">Municipio *</Label>
                    <Input
                      id="obra_municipio"
                      value={formData.obra_municipio}
                      onChange={(e) => handleInputChange('obra_municipio', e.target.value)}
                      placeholder="Madrid"
                    />
                  </div>
                  <div>
                    <Label htmlFor="obra_provincia">Provincia</Label>
                    <Input
                      id="obra_provincia"
                      value={formData.obra_provincia}
                      onChange={(e) => handleInputChange('obra_provincia', e.target.value)}
                      placeholder="Madrid"
                    />
                  </div>
                </div>

                {/* Row 3: Dimensiones de la piscina (necesarias para visualizar en el mapa) */}
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                    <Droplet className="w-4 h-4" />
                    Dimensiones de la Piscina (para visualizar en el mapa)
                  </h4>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <Label htmlFor="piscina_length">Largo (m) *</Label>
                      <Input
                        id="piscina_length"
                        type="number"
                        step="0.1"
                        min="1"
                        max="50"
                        value={formData.piscina_length}
                        onChange={(e) => handleInputChange('piscina_length', e.target.value)}
                        placeholder="8"
                      />
                    </div>
                    <div>
                      <Label htmlFor="piscina_width">Ancho (m) *</Label>
                      <Input
                        id="piscina_width"
                        type="number"
                        step="0.1"
                        min="1"
                        max="25"
                        value={formData.piscina_width}
                        onChange={(e) => handleInputChange('piscina_width', e.target.value)}
                        placeholder="4"
                      />
                    </div>
                    <div>
                      <Label htmlFor="piscina_prof_min">Prof. Mínima (m) *</Label>
                      <Input
                        id="piscina_prof_min"
                        type="number"
                        step="0.1"
                        min="0.3"
                        max="3"
                        value={formData.piscina_prof_min}
                        onChange={(e) => handleInputChange('piscina_prof_min', e.target.value)}
                        placeholder="1.0"
                      />
                    </div>
                    <div>
                      <Label htmlFor="piscina_prof_max">Prof. Máxima (m) *</Label>
                      <Input
                        id="piscina_prof_max"
                        type="number"
                        step="0.1"
                        min="0.5"
                        max="4"
                        value={formData.piscina_prof_max}
                        onChange={(e) => handleInputChange('piscina_prof_max', e.target.value)}
                        placeholder="2.0"
                      />
                    </div>
                  </div>
                </div>

                {/* Row 4: Mapa Avanzado con Catastro y dibujo de piscina */}
                <div>
                  <Label className="mb-2 block text-lg font-semibold">Ubicación y Orientación de la Piscina *</Label>
                  <PoolMapAdvanced
                    center={formData.latitude && formData.longitude ? [formData.latitude, formData.longitude] : [40.4168, -3.7038]}
                    zoom={formData.latitude && formData.longitude ? 19 : 6}
                    onLocationSelect={(data) => {
                      handleInputChange('latitude', data.latitude);
                      handleInputChange('longitude', data.longitude);
                      if (data.rotation !== undefined) {
                        handleInputChange('pool_rotation', data.rotation);
                      }
                    }}
                    onMapCapture={(imageData) => {
                      handleInputChange('map_image', imageData);
                    }}
                    poolDimensions={
                      formData.piscina_length && formData.piscina_width ? {
                        length: parseFloat(formData.piscina_length) || 8,
                        width: parseFloat(formData.piscina_width) || 4
                      } : null
                    }
                    initialRotation={formData.pool_rotation || 0}
                    editable={true}
                  />
                  {formData.latitude && formData.longitude && (
                    <p className="text-xs text-green-600 mt-2 font-semibold">
                      ✓ Ubicación seleccionada: {formData.latitude.toFixed(6)}, {formData.longitude.toFixed(6)} | Orientación: {formData.pool_rotation || 0}°
                    </p>
                  )}
                  {formData.map_image && (
                    <p className="text-xs text-green-600 font-semibold">
                      ✓ Imagen del mapa capturada para el PDF
                    </p>
                  )}
                </div>

                {/* Row 5: Referencia Catastral */}
                <div>
                  <Label htmlFor="referencia_catastral">Referencia Catastral *</Label>
                  <div className="flex gap-2">
                    <Input
                      id="referencia_catastral"
                      data-testid="referencia-catastral-input"
                      value={formData.referencia_catastral}
                      onChange={(e) => handleInputChange('referencia_catastral', e.target.value.toUpperCase())}
                      className="flex-1"
                      placeholder="1234567AB1234C0001AB"
                      maxLength={20}
                    />
                    <Button
                      data-testid="validate-catastral-button"
                      onClick={handleValidateCatastral}
                      disabled={validatingCatastral}
                      variant="outline"
                    >
                      {validatingCatastral ? 'Validando...' : 'Validar'}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Formato: 20 caracteres
                  </p>
                </div>
              </div>
            )}

            {/* Step 7: Elementos de Seguridad */}
            {step === 7 && (
              <div className="space-y-4">
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
                  <p className="text-sm text-blue-900 font-semibold">
                    Según el CTE DB-SUA, es obligatorio contar con al menos uno de estos elementos de seguridad
                  </p>
                </div>
                
                {/* Checkboxes principales */}
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 p-4 border border-slate-200 rounded-lg">
                    <Checkbox
                      id="has_cover"
                      data-testid="has-cover-checkbox"
                      checked={formData.has_cover}
                      onCheckedChange={(checked) => handleInputChange('has_cover', checked)}
                    />
                    <Label htmlFor="has_cover" className="cursor-pointer flex-1">
                      <div className="font-semibold">Cobertor de Seguridad</div>
                      <div className="text-sm text-slate-600">Cobertor rígido o de lámina</div>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3 p-4 border border-slate-200 rounded-lg">
                    <Checkbox
                      id="has_fence"
                      data-testid="has-fence-checkbox"
                      checked={formData.has_fence}
                      onCheckedChange={(checked) => handleInputChange('has_fence', checked)}
                    />
                    <Label htmlFor="has_fence" className="cursor-pointer flex-1">
                      <div className="font-semibold">Valla de Seguridad</div>
                      <div className="text-sm text-slate-600">Cerramiento perimetral con acceso controlado</div>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3 p-4 border border-slate-200 rounded-lg">
                    <Checkbox
                      id="has_alarm"
                      data-testid="has-alarm-checkbox"
                      checked={formData.has_alarm}
                      onCheckedChange={(checked) => handleInputChange('has_alarm', checked)}
                    />
                    <Label htmlFor="has_alarm" className="cursor-pointer flex-1">
                      <div className="font-semibold">Alarma de Seguridad</div>
                      <div className="text-sm text-slate-600">Sistema de detección de inmersión</div>
                    </Label>
                  </div>
                </div>

                {/* Detalles adicionales de seguridad */}
                <div className="pt-4 border-t border-slate-200 space-y-4">
                  <h4 className="font-semibold text-slate-700">Detalles adicionales (opcionales)</h4>
                  
                  <div>
                    <Label htmlFor="vallado">Descripción del Vallado</Label>
                    <Input
                      id="vallado"
                      value={formData.vallado}
                      onChange={(e) => handleInputChange('vallado', e.target.value)}
                      placeholder="Ej: Valla metálica de 1.2m con puerta con cierre automático"
                    />
                  </div>

                  <div>
                    <Label htmlFor="cubierta_seguridad">Descripción de la Cubierta de Seguridad</Label>
                    <Input
                      id="cubierta_seguridad"
                      value={formData.cubierta_seguridad}
                      onChange={(e) => handleInputChange('cubierta_seguridad', e.target.value)}
                      placeholder="Ej: Cobertor de lamas de PVC enrollable"
                    />
                  </div>

                  <div>
                    <Label htmlFor="accesos_piscina">Accesos a la Piscina</Label>
                    <Input
                      id="accesos_piscina"
                      value={formData.accesos_piscina}
                      onChange={(e) => handleInputChange('accesos_piscina', e.target.value)}
                      placeholder="Ej: Escalera de acero inoxidable con pasamanos"
                    />
                  </div>

                  <div>
                    <Label htmlFor="senalizacion_prof">Señalización de Profundidad</Label>
                    <Input
                      id="senalizacion_prof"
                      value={formData.senalizacion_prof}
                      onChange={(e) => handleInputChange('senalizacion_prof', e.target.value)}
                      placeholder="Ej: Marcas de profundidad en el borde de la piscina"
                    />
                  </div>

                  <div>
                    <Label htmlFor="dispositivos_seguridad">Otros Dispositivos de Seguridad</Label>
                    <Input
                      id="dispositivos_seguridad"
                      value={formData.dispositivos_seguridad}
                      onChange={(e) => handleInputChange('dispositivos_seguridad', e.target.value)}
                      placeholder="Ej: Flotador salvavidas, pértiga de rescate"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 8: Empresa Instaladora */}
            {step === 8 && (
              <div className="space-y-4">
                <div className="bg-slate-50 border-l-4 border-slate-500 p-4 mb-4">
                  <p className="text-sm text-slate-700">
                    Datos de la empresa que realiza la instalación de la piscina
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="instaladora_nombre">Nombre de la Empresa *</Label>
                    <Input
                      id="instaladora_nombre"
                      data-testid="instaladora-nombre-input"
                      value={formData.instaladora_nombre}
                      onChange={(e) => handleInputChange('instaladora_nombre', e.target.value)}
                      placeholder="Piscinas ABC S.L."
                    />
                  </div>
                  <div>
                    <Label htmlFor="instaladora_cif">CIF *</Label>
                    <Input
                      id="instaladora_cif"
                      data-testid="instaladora-cif-input"
                      value={formData.instaladora_cif}
                      onChange={(e) => handleInputChange('instaladora_cif', e.target.value.toUpperCase())}
                      placeholder="B12345678"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="instaladora_direccion">Dirección</Label>
                  <Input
                    id="instaladora_direccion"
                    value={formData.instaladora_direccion}
                    onChange={(e) => handleInputChange('instaladora_direccion', e.target.value)}
                    placeholder="Calle Industrial, 10"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="instaladora_cp">CP</Label>
                    <Input
                      id="instaladora_cp"
                      value={formData.instaladora_cp}
                      onChange={(e) => handleInputChange('instaladora_cp', e.target.value)}
                      maxLength={5}
                      placeholder="28001"
                    />
                  </div>
                  <div>
                    <Label htmlFor="instaladora_municipio">Municipio</Label>
                    <Input
                      id="instaladora_municipio"
                      value={formData.instaladora_municipio}
                      onChange={(e) => handleInputChange('instaladora_municipio', e.target.value)}
                      placeholder="Madrid"
                    />
                  </div>
                  <div>
                    <Label htmlFor="instaladora_provincia">Provincia</Label>
                    <Input
                      id="instaladora_provincia"
                      value={formData.instaladora_provincia}
                      onChange={(e) => handleInputChange('instaladora_provincia', e.target.value)}
                      placeholder="Madrid"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="instaladora_telefono">Teléfono</Label>
                    <Input
                      id="instaladora_telefono"
                      value={formData.instaladora_telefono}
                      onChange={(e) => handleInputChange('instaladora_telefono', e.target.value)}
                      placeholder="910123456"
                    />
                  </div>
                  <div>
                    <Label htmlFor="instaladora_email">Email</Label>
                    <Input
                      id="instaladora_email"
                      type="email"
                      value={formData.instaladora_email}
                      onChange={(e) => handleInputChange('instaladora_email', e.target.value)}
                      placeholder="info@piscinasabc.com"
                    />
                  </div>
                </div>

              </div>
            )}

            {/* Step 9: Presupuesto y Firma */}
            {step === 9 && (
              <div className="space-y-4">
                <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
                  <p className="text-sm text-green-800">
                    Datos económicos y de firma del proyecto
                  </p>
                </div>

                <div>
                  <Label htmlFor="presupuesto_pem">Presupuesto de Ejecución Material (PEM) *</Label>
                  <div className="relative">
                    <Input
                      id="presupuesto_pem"
                      data-testid="presupuesto-pem-input"
                      type="number"
                      step="0.01"
                      value={formData.presupuesto_pem}
                      onChange={(e) => handleInputChange('presupuesto_pem', e.target.value)}
                      className="pr-10"
                      placeholder="15000.00"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">€</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Importe total de la obra sin IVA
                  </p>
                </div>

                <div className="pt-4 border-t border-slate-200 space-y-4">
                  <h4 className="font-semibold text-slate-700">Datos de Firma</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="lugar_firma">Lugar de Firma</Label>
                      <Input
                        id="lugar_firma"
                        value={formData.lugar_firma}
                        onChange={(e) => handleInputChange('lugar_firma', e.target.value)}
                        placeholder="Madrid"
                      />
                    </div>
                    <div>
                      <Label htmlFor="fecha_firma">Fecha de Firma</Label>
                      <Input
                        id="fecha_firma"
                        type="date"
                        value={formData.fecha_firma}
                        onChange={(e) => handleInputChange('fecha_firma', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Resumen del servicio seleccionado */}
                <div className="pt-4 border-t border-slate-200">
                  <div className={`p-4 rounded-lg ${serviceType === 'con_firma' ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50 border border-slate-200'}`}>
                    <h4 className="font-semibold text-slate-800 mb-2">
                      Servicio seleccionado: {serviceType === 'con_firma' ? 'CON FIRMA (249€)' : 'SIN FIRMA (59€)'}
                    </h4>
                    <p className="text-sm text-slate-600">
                      {serviceType === 'con_firma' 
                        ? 'Incluye revisión por técnico y firma digital del documento. Se te solicitarán fotos de la piscina en el siguiente paso.'
                        : 'Documento técnico sin firma profesional. Ideal para trámites que no requieren visado.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 10: Fotografías (solo CON FIRMA) */}
            {step === 10 && serviceType === 'con_firma' && (
              <div className="space-y-4">
                <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-4">
                  <p className="text-sm text-orange-800">
                    <strong>Importante:</strong> Para el servicio CON FIRMA, necesitamos fotografías de la piscina construida para la revisión técnica.
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
                    <div className="mb-4">
                      <svg className="mx-auto h-12 w-12 text-slate-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <p className="text-slate-600 mb-2">
                      Arrastra y suelta las fotos aquí, o haz clic para seleccionar
                    </p>
                    <p className="text-xs text-slate-500">
                      PNG, JPG hasta 10MB cada una (máximo 5 fotos)
                    </p>
                    <input
                      type="file"
                      id="fotos_piscina"
                      multiple
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        handleInputChange('fotos_piscina', files);
                        toast.success(`${files.length} foto(s) seleccionada(s)`);
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="mt-4"
                      onClick={() => document.getElementById('fotos_piscina')?.click()}
                    >
                      Seleccionar Fotos
                    </Button>
                  </div>

                  {formData.fotos_piscina && formData.fotos_piscina.length > 0 && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-sm text-green-800 font-semibold mb-2">
                        ✓ {formData.fotos_piscina.length} foto(s) seleccionada(s)
                      </p>
                      <ul className="text-sm text-green-700 space-y-1">
                        {formData.fotos_piscina.map((file, index) => (
                          <li key={index}>• {file.name}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-2">Fotos recomendadas:</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• Vista general de la piscina</li>
                      <li>• Detalle de la depuradora y filtro</li>
                      <li>• Elementos de seguridad instalados</li>
                      <li>• Accesos y escaleras</li>
                      <li>• Referencia catastral visible (si está disponible)</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between pt-6 border-t border-slate-200">
              <Button
                data-testid="back-button"
                onClick={handleBack}
                disabled={step === 1}
                variant="outline"
                className="btn-hover-lift"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Atrás
              </Button>
              <Button
                data-testid={step === totalSteps ? "submit-button" : "next-button"}
                onClick={handleNext}
                disabled={!isStepValid() || loading}
                className="bg-primary hover:bg-primary-hover text-white btn-hover-lift"
              >
                {loading ? (
                  'Procesando...'
                ) : step === totalSteps ? (
                  <>Calcular y Continuar</>
                ) : (
                  <>
                    Siguiente
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Preview Page
const PreviewPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`);
      setProject(response.data);
    } catch (error) {
      console.error('Error fetching project:', error);
      toast.error('Error al cargar el proyecto');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F1F7FD] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-slate-600">Cargando proyecto...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-[#F1F7FD] flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-slate-600 mb-4">Proyecto no encontrado</p>
            <Button onClick={() => navigate('/')}>Volver al inicio</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { pool_data, calculations } = project;

  return (
    <div className="min-h-screen bg-[#F1F7FD] py-12">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-primary hover:text-primary-hover mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio
          </button>
          <h1 className="font-outfit text-4xl font-bold text-slate-900 mb-2">
            Resumen del Proyecto {project.project_number || ''}
          </h1>
          <p className="text-slate-600">
            {(project.service_type || pool_data?.service_type) === 'con_firma' ? 'CON FIRMA' : 'SIN FIRMA'} • 
            Ref: <span className="font-mono text-sm">{projectId.slice(0, 8)}</span>
          </p>
        </div>

        {/* Calculations Grid - Control Room Style */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-white border-primary border-2 shadow-lg">
            <CardHeader>
              <CardTitle className="text-sm text-slate-600 uppercase tracking-wide">Volumen Total</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-mono text-4xl font-bold text-primary">
                {calculations.volume_m3}
              </div>
              <div className="text-slate-600 text-sm mt-1">metros cúbicos</div>
            </CardContent>
          </Card>

          <Card className="bg-white border-primary border-2 shadow-lg">
            <CardHeader>
              <CardTitle className="text-sm text-slate-600 uppercase tracking-wide">Superficie</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-mono text-4xl font-bold text-primary">
                {calculations.surface_area_m2}
              </div>
              <div className="text-slate-600 text-sm mt-1">metros cuadrados</div>
            </CardContent>
          </Card>

          <Card className="bg-white border-primary border-2 shadow-lg">
            <CardHeader>
              <CardTitle className="text-sm text-slate-600 uppercase tracking-wide">Prof. Media</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-mono text-4xl font-bold text-primary">
                {calculations.average_depth_m}
              </div>
              <div className="text-slate-600 text-sm mt-1">metros</div>
            </CardContent>
          </Card>
        </div>

        {/* Technical Details */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="font-outfit flex items-center gap-2">
                <Calculator className="w-5 h-5 text-primary" />
                Cálculos Hidráulicos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-600">Caudal bomba recomendado:</span>
                <span className="font-mono font-semibold">{calculations.recommended_pump_flow} m³/h</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-600">Tiempo de renovación:</span>
                <span className="font-mono font-semibold">{calculations.turnover_time_hours} horas</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-slate-600">Potencia calefacción:</span>
                <span className="font-mono font-semibold">{calculations.estimated_heating_kw} kW</span>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="font-outfit flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                Equipamiento y Seguridad
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-600">Filtración:</span>
                <span className="font-semibold capitalize">{pool_data.filtration_type}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-600">Cobertor:</span>
                <span className={pool_data.has_cover ? 'text-green-600 font-semibold' : 'text-slate-400'}>
                  {pool_data.has_cover ? '✓ Sí' : '✗ No'}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-600">Valla:</span>
                <span className={pool_data.has_fence ? 'text-green-600 font-semibold' : 'text-slate-400'}>
                  {pool_data.has_fence ? '✓ Sí' : '✗ No'}
                </span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-slate-600">Alarma:</span>
                <span className={pool_data.has_alarm ? 'text-green-600 font-semibold' : 'text-slate-400'}>
                  {pool_data.has_alarm ? '✓ Sí' : '✗ No'}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* CTA to Payment */}
        <Card className="bg-gradient-to-br from-primary to-secondary text-white shadow-xl">
          <CardContent className="p-8 text-center">
            <Lock className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h2 className="font-outfit text-2xl font-bold mb-2">
              ¿Todo correcto? Completa el pago para descargar
            </h2>
            <p className="text-blue-100 mb-6">
              Recibirás tu documentación completa en PDF
            </p>
            <div className="flex gap-4 justify-center">
              <Button
                data-testid="proceed-to-payment-button"
                onClick={() => navigate(`/payment/${projectId}`)}
                size="lg"
                className="bg-white text-primary hover:bg-blue-50 font-bold px-8 py-6 rounded-lg btn-hover-lift"
              >
                Proceder al Pago
              </Button>
              <Button
                onClick={() => navigate('/form')}
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10"
              >
                Crear Nuevo Proyecto
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Payment Page
const PaymentPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState('sin_firma_individual');

  const handlePayment = async () => {
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/payments/checkout`, {
        project_id: projectId,
        package_id: selectedPackage,
        origin_url: originUrl
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Error al iniciar el pago. Por favor, inténtalo de nuevo.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F1F7FD] py-12">
      <div className="max-w-4xl mx-auto px-4">
        <div className="mb-8 text-center">
          <h1 className="font-outfit text-4xl font-bold text-slate-900 mb-2">
            Selecciona tu Plan
          </h1>
          <p className="text-slate-600">Elige la opción que mejor se adapte a tus necesidades</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <Card 
            className={`cursor-pointer transition-all ${
              selectedPackage === 'sin_firma_individual' 
                ? 'border-2 border-primary shadow-lg ring-2 ring-blue-100' 
                : 'border-2 border-slate-200 hover:border-primary'
            }`}
            onClick={() => setSelectedPackage('sin_firma_individual')}
          >
            <CardHeader>
              <div className="flex justify-between items-start">
                <CardTitle className="font-outfit text-2xl">SIN FIRMA - Individual</CardTitle>
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  selectedPackage === 'sin_firma_individual' ? 'border-primary bg-primary' : 'border-slate-300'
                }`}>
                  {selectedPackage === 'sin_firma_individual' && <CheckCircle2 className="w-4 h-4 text-white" />}
                </div>
              </div>
              <div className="mt-4">
                <span className="font-outfit text-5xl font-bold text-primary">59€</span>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                  <span>1 proyecto completo</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                  <span>PDF descargable</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                  <span>Cálculos automáticos</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card 
            className={`cursor-pointer transition-all relative ${
              selectedPackage === 'sin_firma_pack5' 
                ? 'border-2 border-primary shadow-lg ring-2 ring-blue-100' 
                : 'border-2 border-slate-200 hover:border-primary'
            }`}
            onClick={() => setSelectedPackage('sin_firma_pack5')}
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FF6D00] text-white px-4 py-1 rounded-full text-sm font-semibold">
              Ahorra 50€
            </div>
            <CardHeader>
              <div className="flex justify-between items-start">
                <CardTitle className="font-outfit text-2xl">SIN FIRMA - Pack 5</CardTitle>
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  selectedPackage === 'sin_firma_pack5' ? 'border-primary bg-primary' : 'border-slate-300'
                }`}>
                  {selectedPackage === 'sin_firma_pack5' && <CheckCircle2 className="w-4 h-4 text-white" />}
                </div>
              </div>
              <div className="mt-4">
                <span className="font-outfit text-5xl font-bold text-primary">245€</span>
                <span className="text-slate-600 ml-2">
                  <span className="line-through text-lg">295€</span>
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                  <span>5 proyectos incluidos</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                  <span>Solo 49€ por dossier</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                  <span>Sin caducidad</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        <Card className="glass-card shadow-lg">
          <CardContent className="p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-outfit text-xl font-semibold">Total a pagar</h3>
                <p className="text-sm text-slate-600">Pago seguro con Stripe</p>
              </div>
              <div className="font-outfit text-4xl font-bold text-primary">
                {selectedPackage === 'sin_firma_individual' ? '59€' : '245€'}
              </div>
            </div>
            <Button
              data-testid="confirm-payment-button"
              onClick={handlePayment}
              disabled={loading}
              className="w-full bg-primary hover:bg-primary-hover text-white font-bold py-6 text-lg rounded-lg btn-hover-lift"
            >
              {loading ? 'Procesando...' : 'Proceder al Pago Seguro'}
            </Button>
            <p className="text-xs text-center text-slate-500 mt-4">
              🔒 Pago 100% seguro. Tus datos están protegidos con encriptación SSL.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Success/Download Page
const SuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [projectId, setProjectId] = useState(null);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) {
      setStatus('error');
      toast.error('Tiempo de espera agotado. Por favor, contacta con soporte.');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setProjectId(response.data.project_id);
        toast.success('¡Pago completado con éxito!');
      } else if (response.data.status === 'expired') {
        setStatus('error');
        toast.error('La sesión de pago ha expirado.');
      } else {
        setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
      toast.error('Error al verificar el pago.');
    }
  };

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const handleDownload = async (format) => {
    try {
      const response = await axios.get(
        `${API}/projects/${projectId}/download/${format}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pool_permit_${projectId.slice(0, 8)}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Documento ${format.toUpperCase()} descargado`);
    } catch (error) {
      console.error(`Error downloading ${format}:`, error);
      toast.error('Error al descargar el documento');
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-[#F1F7FD] flex items-center justify-center">
        <Card className="max-w-md glass-card">
          <CardContent className="pt-6 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <h2 className="font-outfit text-xl font-semibold mb-2">Verificando pago...</h2>
            <p className="text-slate-600">Por favor espera mientras confirmamos tu pago</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-[#F1F7FD] flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">✗</span>
            </div>
            <h2 className="font-outfit text-xl font-semibold mb-2">Error en el pago</h2>
            <p className="text-slate-600 mb-6">
              Hubo un problema al procesar tu pago. Por favor, inténtalo de nuevo.
            </p>
            <Button onClick={() => navigate('/')}>Volver al inicio</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F1F7FD] py-12">
      <div className="max-w-3xl mx-auto px-4">
        <Card className="glass-card shadow-xl">
          <CardContent className="pt-12 pb-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="font-outfit text-4xl font-bold text-slate-900 mb-4">
              ¡Pago Completado!
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              Tu documentación técnica está lista para descargar
            </p>

            <div className="bg-blue-50 border-2 border-primary rounded-lg p-6 mb-8">
              <p className="text-sm text-slate-600 mb-2">ID del Proyecto</p>
              <p className="font-mono text-xl font-bold text-primary">
                {projectId?.slice(0, 8)}
              </p>
            </div>

            <div className="mb-8">
              <Button
                data-testid="download-pdf-button"
                onClick={() => handleDownload('pdf')}
                size="lg"
                className="w-full bg-primary hover:bg-primary-hover text-white font-semibold py-6 rounded-lg btn-hover-lift"
              >
                <Download className="w-5 h-5 mr-2" />
                Descargar PDF
              </Button>
            </div>

            <div className="border-t border-slate-200 pt-6">
              <p className="text-sm text-slate-600 mb-4">
                ¿Necesitas otro dossier?
              </p>
              <Button
                data-testid="create-another-button"
                onClick={() => navigate('/form')}
                variant="outline"
                className="btn-hover-lift"
              >
                Crear Nuevo Proyecto
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="mt-8 text-center text-sm text-slate-600">
          <p>¿Preguntas? Contacta con nosotros en info@legalizatupiscina.com</p>
        </div>
      </div>
    </div>
  );
};

// Service Selection Page
const ServiceSelectionPage = () => {
  const navigate = useNavigate();
  const [selectedService, setSelectedService] = useState(null);

  const handleContinue = () => {
    if (selectedService) {
      navigate('/form', { state: { serviceType: selectedService } });
    }
  };

  return (
    <div className="min-h-screen bg-[#F1F7FD]">
      <div className="noise-texture" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <img src="/poollex-logo.png" alt="PoolLex.io" className="h-16 w-auto" />
            <span className="font-outfit text-3xl font-bold text-[#1E88E5]">PoolLex.io</span>
          </div>
          <Button onClick={() => navigate('/')} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
        </div>

        <div className="text-center mb-12">
          <h1 className="font-outfit text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Selecciona tu Servicio
          </h1>
          <p className="text-lg text-slate-600">
            Elige el tipo de proyecto que necesitas
          </p>
        </div>

        {/* Service Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-12">
          {/* Servicio SIN FIRMA */}
          <Card 
            className={`cursor-pointer transition-all bg-white ${
              selectedService === 'sin_firma' 
                ? 'border-2 border-primary shadow-xl ring-2 ring-blue-100' 
                : 'border-2 border-slate-200 hover:border-primary hover:shadow-lg'
            }`}
            onClick={() => setSelectedService('sin_firma')}
          >
            <CardHeader>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <CardTitle className="font-outfit text-2xl text-slate-900">Proyecto SIN FIRMA</CardTitle>
                  <p className="text-sm text-slate-600 mt-2">Documentación técnica automatizada</p>
                </div>
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  selectedService === 'sin_firma' ? 'border-primary bg-primary' : 'border-slate-300'
                }`}>
                  {selectedService === 'sin_firma' && <CheckCircle2 className="w-4 h-4 text-white" />}
                </div>
              </div>
              
              <div className="space-y-2 mb-4">
                <div className="flex items-baseline gap-2">
                  <span className="font-outfit text-4xl font-bold text-primary">59€</span>
                  <span className="text-slate-600">por proyecto</span>
                </div>
                <div className="text-sm text-slate-600">
                  Pack 5: <span className="font-semibold text-primary">245€</span> (49€/proyecto)
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Proyecto técnico generado automáticamente</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Documento en PDF</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Cálculos geométricos e hidráulicos</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Estructura técnica para revisión posterior</span>
                </div>
              </div>

              <div className="bg-slate-50 border-l-4 border-slate-400 p-3 mb-4">
                <p className="text-xs font-semibold text-slate-700 mb-2">❌ NO INCLUYE:</p>
                <ul className="text-xs text-slate-600 space-y-1">
                  <li>• Firma técnica ni visado colegial</li>
                  <li>• Revisión por técnico colegiado</li>
                  <li>• Garantía de aceptación por ayuntamientos</li>
                </ul>
              </div>

              <div className="bg-slate-100 p-3 rounded text-xs text-slate-700">
                <strong>Aviso legal:</strong> Este servicio genera documentación técnica automatizada sin firma. 
                El cliente es responsable de su revisión y validación.
              </div>
            </CardContent>
          </Card>

          {/* Servicio CON FIRMA */}
          <Card 
            className={`cursor-pointer transition-all relative bg-white ${
              selectedService === 'con_firma' 
                ? 'border-2 border-primary shadow-xl ring-2 ring-blue-100' 
                : 'border-2 border-slate-200 hover:border-primary hover:shadow-lg'
            }`}
            onClick={() => setSelectedService('con_firma')}
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white px-4 py-1 rounded-full text-sm font-semibold">
              Recomendado
            </div>
            <CardHeader>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <CardTitle className="font-outfit text-2xl text-slate-900">Proyecto CON FIRMA</CardTitle>
                  <p className="text-sm text-slate-600 mt-2">Firmado digitalmente por ingeniero</p>
                </div>
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  selectedService === 'con_firma' ? 'border-primary bg-primary' : 'border-slate-300'
                }`}>
                  {selectedService === 'con_firma' && <CheckCircle2 className="w-4 h-4 text-white" />}
                </div>
              </div>
              
              <div className="space-y-2 mb-4">
                <div className="flex items-baseline gap-2">
                  <span className="font-outfit text-4xl font-bold text-primary">249€</span>
                  <span className="text-slate-600">por proyecto</span>
                </div>
                <div className="text-sm text-green-700 font-semibold">
                  ⚡ Entrega en menos de 48h laborables
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="bg-blue-50 border-l-4 border-blue-500 p-3 mb-4">
                <p className="text-xs font-semibold text-blue-900 mb-2">✅ REQUISITOS:</p>
                <ul className="text-xs text-blue-800 space-y-1">
                  <li>• Piscina ya construida</li>
                  <li>• Envío de fotografías reales obligatorio</li>
                  <li>• Información veraz y completa</li>
                </ul>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Revisión técnica manual del proyecto</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Ajustes según información aportada</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm"><strong>Firma digital</strong> por Víctor Saiz Vela - Ingeniero Industrial</span>
                </div>
              </div>

              <div className="bg-slate-50 border-l-4 border-slate-400 p-3 mb-4">
                <p className="text-xs font-semibold text-slate-700 mb-2">❌ NO INCLUYE:</p>
                <ul className="text-xs text-slate-600 space-y-1">
                  <li>• Visado colegial</li>
                  <li>• Presentación en ayuntamientos</li>
                  <li>• Gestión de licencias urbanísticas</li>
                </ul>
              </div>

              <div className="bg-slate-100 p-3 rounded text-xs text-slate-700">
                <strong>Aviso legal:</strong> La firma del proyecto no incluye visado colegial ni garantiza 
                la concesión de licencias. La aceptación dependerá del organismo competente.
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Continue Button */}
        <div className="max-w-md mx-auto">
          <Button
            data-testid="continue-to-form-button"
            onClick={handleContinue}
            disabled={!selectedService}
            size="lg"
            className="w-full bg-primary hover:bg-primary-hover text-white font-bold py-6 text-lg rounded-lg btn-hover-lift"
          >
            {selectedService ? 'Continuar con ' + (selectedService === 'sin_firma' ? 'SIN FIRMA' : 'CON FIRMA') : 'Selecciona un servicio'}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Client Area Wrapper - maneja autenticación de clientes
const ClientAreaWrapper = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('client_token');
    if (token) {
      // Verificar que el token es válido
      axios.get(`${API}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(() => {
        setIsAuthenticated(true);
      }).catch(() => {
        localStorage.removeItem('client_token');
        localStorage.removeItem('client_data');
        setIsAuthenticated(false);
      }).finally(() => {
        setCheckingAuth(false);
      });
    } else {
      setCheckingAuth(false);
    }
  }, []);

  const handleAuthSuccess = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
  };

  const handleNewProject = () => {
    navigate('/select-service');
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-[#F1F7FD] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <ClientAuth onAuthSuccess={handleAuthSuccess} />;
  }

  return <ClientDashboard onLogout={handleLogout} onNewProject={handleNewProject} />;
};

// Sprint 119.104 — Fallback UI mentre carrega un chunk lazy.
// Spinner blau centrat sobre bg-slate-50 (patró de InstallerDashboard).
// Es mostra només quan la ruta destí és lazy i el chunk encara no està al cache;
// la home (LandingPageV2 sync) no dispara Suspense.
const PageFallback = () => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="page-fallback">
    <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

// Main App Component
function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <Toaster position="top-center" />
        <BrowserRouter>
          {/* Sprint 132 — Manté i18n.language alineat amb la URL a cada
              navegació i ofereix (sense redirigir) la versió en l'idioma
              del navegador si n'hi ha una de diferent. Ha d'anar DINS del
              Router però FORA de <Routes> per veure tots els canvis de
              ruta. Veure components/LanguageSync.js. */}
          <LanguageSync />
          <Suspense fallback={<PageFallback />}>
          <Routes>
            {/* Sprint 119.39 — Rutes prefixades per idioma. Cada URL amb
                prefix força l'idioma via LangRoute + segueix mostrant la
                mateixa pàgina. Google indexa aquestes com a versions
                canòniques per hreflang. Cobrim les 7 pàgines clau del
                pitch sostenibilitat + landing. */}
            {SUPPORTED_LANGS.flatMap(lang => [
              <Route key={`${lang}-home`} path={`/${lang}`} element={<LangRoute lang={lang}><LandingPageV2 /></LangRoute>} />,
              <Route key={`${lang}-ficha`} path={`/${lang}/ficha-tecnica`} element={<LangRoute lang={lang}><PassportGenerator /></LangRoute>} />,
              <Route key={`${lang}-init`} path={`/${lang}/initiative`} element={<LangRoute lang={lang}><InitiativePage /></LangRoute>} />,
              <Route key={`${lang}-inst`} path={`/${lang}/instaladores`} element={<LangRoute lang={lang}><InstalladoresPage /></LangRoute>} />,
              <Route key={`${lang}-sello`} path={`/${lang}/solicitar-sello`} element={<LangRoute lang={lang}><SolicitarSelloPage /></LangRoute>} />,
              <Route key={`${lang}-check`} path={`/${lang}/pool-check`} element={<LangRoute lang={lang}><PoolCheckLanding /></LangRoute>} />,
              <Route key={`${lang}-calc`} path={`/${lang}/calculadoras`} element={<LangRoute lang={lang}><CalculadorasHub /></LangRoute>} />,
              // Sprint 119.119c — Rutes prefixades per 7 pàgines addicionals (fix Bing SEO duplicates + hreflang per idioma)
              <Route key={`${lang}-why`} path={`/${lang}/why-passport`} element={<LangRoute lang={lang}><WhyPassportPage /></LangRoute>} />,
              <Route key={`${lang}-early`} path={`/${lang}/early-access`} element={<LangRoute lang={lang}><EarlyAccessPage /></LangRoute>} />,
              <Route key={`${lang}-doc`} path={`/${lang}/documentar-piscina`} element={<LangRoute lang={lang}><DocumentarPiscinaPage /></LangRoute>} />,
              <Route key={`${lang}-legal`} path={`/${lang}/piscina-legal`} element={<LangRoute lang={lang}><PiscinaLegalPage /></LangRoute>} />,
              <Route key={`${lang}-comprar`} path={`/${lang}/comprar-casa-con-piscina`} element={<LangRoute lang={lang}><ComprarCasaPiscinaPage /></LangRoute>} />,
              // Sprint 136 — Les 6 calculadores que faltaven. Estaven traduïdes als
              // 6 idiomes des de feia temps, però no tenien ruta prefixada: des de
              // /nl/calculadoras clicaves una targeta, queies a la ruta canònica
              // sense prefix i la pàgina et sortia en castellà.
              // Canonical: apunta a la ruta ES neutra (no són a HREFLANG_PATHS de
              // SEO.js), així Google consolida i no es fragmenta res.
              // La terrassa NO hi és: només és vàlida en castellà.
            ])}
            {/* Sprint 154 — Rutes de calculadora amb slug per idioma, des de
                src/config/routeSlugs.js. L'idioma per defecte de cada seccio
                viu a la ruta SENSE prefix i es declara mes avall amb la resta
                de rutes neutres; aqui nomes hi van les altres cinc llengues. */}
            {Object.keys(ROUTE_SLUGS).flatMap((key) =>
              Object.keys(ROUTE_SLUGS[key].slugs)
                .filter((lg) => lg !== ROUTE_SLUGS[key].defaultLang)
                .map((lg) => (
                  <Route
                    key={`calc-${key}-${lg}`}
                    path={pathFor(key, lg)}
                    element={<LangRoute lang={lg}>{CALC_PAGE[key]}</LangRoute>}
                  />
                ))
            )}
            <Route path="/" element={<LandingPageV2 />} />
            <Route path="/initiative" element={<InitiativePage />} />
            <Route path="/why-passport" element={<WhyPassportPage />} />
            <Route path="/early-access" element={<EarlyAccessPage />} />
            {/* Ficha Técnica - New URLs */}
            <Route path="/ficha-tecnica" element={<PassportGenerator />} />
            <Route path="/ficha/:poollex_id" element={<PassportPublic />} />
            <Route path="/ficha/:id/edit" element={<PassportEdit />} />
            {/* Redirects from old passport URLs */}
            <Route path="/passport-generator" element={<Navigate to="/ficha-tecnica" replace />} />
            <Route path="/passport/:poollex_id" element={<RedirectToFicha />} />
            <Route path="/compliance-check" element={<ComplianceCheckLanding />} />
            <Route path="/compliance-check/wizard" element={<ComplianceCheckWizard />} />
            <Route path="/compliance-check/result" element={<ComplianceCheckResult />} />
            {/* Pool Check - Same as compliance but with new URL */}
            <Route path="/pool-check" element={<PoolCheckLanding />} />
            <Route path="/pool-check/start" element={<PoolCheckQuick />} />
            <Route path="/pool-check/classic" element={<ComplianceCheckLanding />} />
            <Route path="/pool-check/wizard" element={<ComplianceCheckWizard />} />
            <Route path="/pool-check/result" element={<ComplianceCheckResult />} />
            {/* Instaladores */}
            <Route path="/instaladores" element={<InstalladoresPage />} />
            {/* Sprint 119.5 — Sol·licitud del Sello Piscina Sostenible (Nivell 2) */}
            <Route path="/solicitar-sello" element={<SolicitarSelloPage />} />
            {/* Mantenimiento — Sprint 47: gated by feature flag.
                Sprint 119.91: quan flag OFF (default), redirigeix a
                /calculadoras enlloc de / (consolidem link equity al hub SEO
                actiu). El server.js emet 301 real per crawlers, aquesta
                <Navigate> cobreix client-side routing residual (React Router).
                El codi de MantenimientoPage es manté íntegre per a reactivar
                en el futur via featureFlags.js o admin toggle. */}
            <Route
              path="/mantenimiento"
              element={
                isFeatureEnabled("maintenanceServiceVisible")
                  ? <MantenimientoPage />
                  : <Navigate to="/calculadoras" replace />
              }
            />
            <Route
              path="/mantenimiento-piscina"
              element={
                isFeatureEnabled("maintenanceServiceVisible")
                  ? <MantenimientoPage />
                  : <Navigate to="/calculadoras" replace />
              }
            />
            {/* Profesionales Hub */}
            <Route path="/profesionales" element={<ProfesionalesPage />} />
            {/* Landings de captación */}
            <Route path="/documentar-piscina" element={<DocumentarPiscinaPage />} />
            <Route path="/contratacion-publica" element={<ContratacionPublicaPage />} />
            <Route path="/normativa-agua-piscinas" element={<NormativaAguaPiscinas />} />
            <Route path="/condiciones-piscina" element={<CondicionsPiscinaIndex />} />
            <Route path="/condiciones-piscina/:slug" element={<CondicionsPiscinaMunicipi />} />
            <Route path="/contratacion-publica/monitor" element={<MonitorPiscinasPage />} />
            {/* Sprint 117.8 — /municipios/{slug} interina admin-only.
                Sprint 117.9 — Ara accepta usuari registrat general
                (admin OR installer). LoginChoicePage al middle si no
                autenticat.

                12/10/2026 — la pàgina passa a ser PÚBLICA, però les xifres
                no. El gate ha baixat on havia de ser des del principi: al
                servidor (`backend/municipal_access.py`). Aquest wrapper
                amagava la pàgina mentre els seus dos endpoints servien els
                agregats sencers a qui els demanés sense cap token, i a més
                tallava el camí que ve del monitor de contratación, que
                enllaça el nom de l'ajuntament aquí.

                Segueix amb noindex (ho posa la pàgina): volem que hi arribi
                qui ve del monitor, no omplir Google de fitxes primes. */}
            <Route path="/municipios/:slug" element={<MunicipioPublicPage />} />
            <Route path="/piscina-legal" element={<PiscinaLegalPage />} />
            <Route path="/comprar-casa-con-piscina" element={<ComprarCasaPiscinaPage />} />
            {/* Sprint 119.77 (2026-07-31) — Blog multi-idioma ES/EN/FR/DE/IT.
                Base ES neutra + prefixades /en/blog, /fr/blog, /de/blog,
                /it/blog. Cada article té 5 rutes (1 per idioma) amb slugs
                traduits. LangRoute força l'idioma actiu per SEO consistent. */}
            <Route path="/blog" element={<BlogIndex />} />
            {/* Sprint 119.97 — NL 6è idioma. */}
            {["en", "fr", "de", "it", "nl"].map((lg) => (
              <Route
                key={`${lg}-blog-index`}
                path={`/${lg}/blog`}
                element={<LangRoute lang={lg}><BlogIndex /></LangRoute>}
              />
            ))}
            {ARTICLES.map((a) => (
              <React.Fragment key={a.id}>
                {/* Sprint 119.100 — guard `a.slugs.es` per suportar articles
                    single-language sense slug ES (p.ex. Article 7 EN-only,
                    Article 8 DE-only). Sense guard es generaria /blog/undefined. */}
                {a.slugs.es ? (
                  <Route
                    path={`/blog/${a.slugs.es}`}
                    element={<BlogArticle articleId={a.id} lang="es" />}
                  />
                ) : null}
                {["en", "fr", "de", "it", "nl"].map((lg) => (
                  a.slugs[lg] ? (
                    <Route
                      key={`${a.id}-${lg}`}
                      path={`/${lg}/blog/${a.slugs[lg]}`}
                      element={
                        <LangRoute lang={lg}>
                          <BlogArticle articleId={a.id} lang={lg} />
                        </LangRoute>
                      }
                    />
                  ) : null
                ))}
              </React.Fragment>
            ))}
            {/* Calculadoras Hub */}
            <Route path="/calculadoras" element={<CalculadorasHub />} />
            <Route path="/tools" element={<CalculadorasHub />} />
            {/* Sprint 161/166 — Les tres de /tools/ són la versió ANGLESA. La
                castellana té slug propi (routeSlugs) i es declara amb la resta
                de llengües. Sense LangRoute, en navegar-hi des d'una pàgina
                castellana es quedava l'idioma d'abans. */}
            <Route path="/tools/pool-volume-calculator" element={<LangRoute lang="en"><VolumeCalculator /></LangRoute>} />
            <Route path="/tools/pool-energy-calculator" element={<LangRoute lang="en"><EnergyCalculator /></LangRoute>} />
            <Route path="/tools/pool-water-calculator" element={<LangRoute lang="en"><WaterCalculator /></LangRoute>} />
            {/* New Calculators */}
            <Route path="/calculadora-sal-piscina" element={<PoolSaltCalculator />} />
            <Route path="/calculadora-cloro-piscina" element={<PoolChlorineCalculator />} />
            <Route path="/calculadora-tiempo-filtracion" element={<PoolFiltrationCalculator />} />
            <Route path="/calculadora-bomba-calor-piscina" element={<PoolHeatPumpCalculator />} />
            <Route path="/calculadora-placas-solares-piscina" element={<PoolSolarCalculator />} />
            {/* Sprint 93 — Calculadora càrrega estructural terrassa (CTE DB-SE-AE) */}
            <Route path="/calculadora-terraza-piscina" element={<TerraceLoadCalculator />} />
            <Route path="/calculadora-base-piscina-elevada" element={<PoolBaseCalculator />} />
            <Route path="/configurador-cubierta-piscina" element={<CoverConfigurator />} />
            <Route path="/calculadora-carga-terraza" element={<Navigate to="/calculadora-terraza-piscina" replace />} />
            {/* ICP Calculator redirects to Pool Check (now unified) */}
            <Route path="/calculadora-icp" element={<Navigate to="/pool-check" replace />} />
            <Route path="/calculadora-icm" element={<Navigate to="/pool-check" replace />} />
            <Route path="/icm-calculator" element={<Navigate to="/pool-check" replace />} />
            <Route path="/icp-calculator" element={<Navigate to="/pool-check" replace />} />
            {/* Sprint 119.125-lite — Portal client read-only per promotor.
                Ruta pública sense auth. El token JWT porta scope al project_id
                i la validesa (defualt 90 dies). Generat des Kanban admin. */}
            <Route path="/expediente/:token" element={<ClientExpedientPortal />} />
            <Route path="/thank-you" element={<ThankYouPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/cookies" element={<CookiesPage />} />
            <Route path="/legal" element={<LegalPage />} />
            <Route path="/select-service" element={<ServiceSelectionPage />} />
            <Route path="/form" element={<FormWizard />} />
            <Route path="/preview/:projectId" element={<PreviewPage />} />
            <Route path="/payment/:projectId" element={<PaymentPage />} />
            <Route path="/success" element={<SuccessPage />} />
            {/* Setup Password - Instalador crea su contraseña */}
            <Route path="/setup-password" element={<SetupPassword />} />
            {/* Reset Password - Recuperar contraseña */}
            <Route path="/forgot-password" element={<ResetPassword mode="forgot" />} />
            <Route path="/reset-password" element={<ResetPassword mode="reset" />} />
            {/* Área de Cliente */}
            <Route path="/cliente/*" element={<ClientAreaWrapper />} />
            <Route path="/cliente/login" element={<ClientAreaWrapper />} />
            <Route path="/cliente/dashboard" element={<ClientAreaWrapper />} />
            {/* Installer */}
            <Route path="/installer/login" element={<InstallerLogin />} />
            <Route path="/installer/dashboard" element={<InstallerDashboard />} />
            <Route path="/installer/setup-password" element={<InstallerSetupPassword />} />
            {/* Admin */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/panel" element={
              <RequireAdminAuth>
                <AdminPanel />
              </RequireAdminAuth>
            } />
            {/* Sprint 34 — Catalunya ICM extrapolation map */}
            <Route path="/admin/mapa-icm" element={
              <RequireAdminAuth>
                <IcmMapPage />
              </RequireAdminAuth>
            } />
            {/* Sprint 72 — Fullscreen document editor + template management */}
            <Route path="/admin/projects/:projectId/document-editor" element={
              <RequireAdminAuth>
                <ProjectDocumentEditorPage />
              </RequireAdminAuth>
            } />
            <Route path="/admin/templates" element={
              <RequireAdminAuth>
                <TemplatesPage />
              </RequireAdminAuth>
            } />
            <Route path="/admin/templates/:slug/editor" element={
              <RequireAdminAuth>
                <TemplateEditorPage />
              </RequireAdminAuth>
            } />
            {/* Sprint 84 — Pool detector (illegal pools per municipality) */}
            <Route path="/admin/detector-pools" element={
              <RequireAdminAuth>
                <PoolDetectorPage />
              </RequireAdminAuth>
            } />
            <Route path="/admin/detector-pools/:slug" element={
              <RequireAdminAuth>
                <PoolDetectorMapPage />
              </RequireAdminAuth>
            } />
            {/* Mini-app de posicionamiento manual de piscina sobre el
                catastro. Se entra desde el botón "Posicionar piscina"
                del editor de proyecto en AdminPanel; al guardar
                escribe en pool_data.pool_position_offset_*_m, que es
                lo que el resto del pipeline (DOCX + 5 planos) ya lee. */}
            <Route path="/admin/projects/:projectId/pool-position" element={
              <RequireAdminAuth>
                <PoolPositionEditor />
              </RequireAdminAuth>
            } />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
